import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import {
  APIConstants,
  ClaimStatusConstants,
  CommonAPIConstants,
  convertDateFormat,
  formatAppointMentDate,
  formatDOSDate,
  IntelliBillConstants,
  NewEncounterConstants,
} from "../utilities/Constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf } from "@fortawesome/free-regular-svg-icons";
import {
  faCircleCheck,
  faShieldHalved,
  faMinus,
  faPlus,
  faXmark,
  faTrash,
  faPaperclip,
} from "@fortawesome/free-solid-svg-icons";
import { format } from "date-fns";
import CustomDatePicker from "../components/DatePicker";
import Draggable from "react-draggable";
import "../css/EncounterV2.css";

/* =============================================================================
   New Encounter V2 — same end-to-end flow as Encounter.js but rebuilt in the
   Claim Status V2 design system. Logic / API calls are intentionally kept
   parallel to V1 so behaviour stays identical; only the JSX surface and the
   styling were redesigned.
   ============================================================================= */

const FloatingCellAutocomplete = ({ anchorRef, open, children }) => {
  const [style, setStyle] = useState(null);

  useEffect(() => {
    if (!open || !anchorRef?.current) {
      setStyle(null);
      return undefined;
    }

    const updatePosition = () => {
      const rect = anchorRef.current.getBoundingClientRect();
      const width = Math.max(rect.width, 220);
      const maxHeight = 220;
      const gap = 4;
      const belowSpace = window.innerHeight - rect.bottom - gap;
      const aboveSpace = rect.top - gap;
      const openBelow = belowSpace >= Math.min(maxHeight, aboveSpace);
      const height = Math.min(maxHeight, Math.max(openBelow ? belowSpace : aboveSpace, 120));
      const left = Math.min(Math.max(rect.left, 8), window.innerWidth - width - 8);
      const top = openBelow ? rect.bottom + gap : rect.top - height - gap;

      setStyle({
        position: "fixed",
        top,
        left,
        width,
        maxHeight: height,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchorRef, open]);

  if (!open || !style) return null;

  return createPortal(
    <ul
      className="enc-cell-autocomplete enc-cell-autocomplete-floating"
      style={style}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {children}
    </ul>,
    document.body
  );
};

const Encounter = () => {
  const userData = useSelector((s) => s.user.user);
  const { id } = useParams();
  const userPracticeID = userData ? userData.practiceID : null;
  const userId = userData ? userData.userID : null;
  const sAuthToken = useSelector((s) => s.user.token);

  /* ─── State (parity with V1) ─── */
  const [, setError] = useState(null);
  const [, setLoading] = useState(true);
  const [searchPatientText, setSearchPatientText] = useState("");
  const [searchPatientData, setSearchPatientData] = useState([]);
  const [selectedDOB, setSelectedDOB] = useState("");
  const [selectedPatientId, setSelectedPatientID] = useState("");
  const [patientInsData, setPatientInsData] = useState([]);
  const [selectedInsurance, setSelectedInsurance] = useState("");
  const [selectedPatientInsurance, setSelectedPatientInsurance] = useState("");
  const [selectedSecInsurance, setselectedSecInsurance] = useState("-1");
  const [appointmentDate, setAppointmentDate] = useState("");
  const [claimDetails, setClaimDetails] = useState([]);
  const [approveDate, setApproveDate] = useState("");
  const [, setFromDate] = useState("");
  const [, setToDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [fromDates, setFromDates] = useState([]);
  const [toDates, setToDates] = useState([]);
  const [selectedPracticeProvider, setSelectedPracticeProvider] = useState("");
  const [practiceProvider, setPracticeProvider] = useState([]);
  const [selectedRenderingProvider, setSelectedRenderingProvider] = useState("");
  const [selectedProvidersCategory, setSelectedProvidersCategory] = useState(0);
  const [locations, setLocations] = useState([]);
  const [cms33aData, setCMS33aData] = useState([]);
  const [cms33bData, setCMS33bData] = useState([]);
  const [BillingNPIs, setBillingNPIs] = useState([]);
  const [ServiceLocations, setServiceLocations] = useState([]);
  const [selectedCMS33a, setSelectedCMS33a] = useState("0 ");
  const [selectedCMS33b, setSelectedCMS33b] = useState("0");
  const [selectedBillingNPI, setSelectedBillingNPI] = useState("0");
  const [providerCategory, setProviderCategory] = useState([]);
  const [practiceTaxonomycodes, setPracticeTaxonomycodes] = useState([]);
  const [selectedFilterProvider, setSelectedFilterProvider] = useState("-1");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedBillingAddress, setSelectedBillingAddress] = useState("");
  const [selectedServiceLocation, setSelectedServicelocation] = useState("");
  const [isBOX32checked, setIsBox32Checked] = useState(0);
  const [selectedResubmitCode, setSelectedResubmitCode] = useState("0");
  const [selectedOriginalClaim, setSelectedOriginalClaim] = useState("");
  const [isPatientSelected, setIsPatientSelected] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [claimRowsData, setClaimRowsData] = useState([]);
  const [selectedInsData] = useState([]);
  const [isInsdata] = useState(true);
  const [btnStatus, setBtnStatus] = useState(null);
  const [denied, setDenied] = useState(false);
  const [showPaidErrorModal, setShowPaidErrorModal] = useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [showConfirmSaveModal, setShowConfirmSaveModal] = useState(false);
  const [showEligible, setShowEligible] = useState(false);
  const [showSecondaryEligible, setShowSecondaryEligible] = useState(false);
  // Eligibility check state — { status, error } per insurance + loading flag.
  // status: "Active" / other payer text; error: payer-returned reason for ineligibility.
  const [priEligibility, setPriEligibility] = useState(null);
  const [secEligibility, setSecEligibility] = useState(null);
  const [priEligLoading, setPriEligLoading] = useState(false);
  const [secEligLoading, setSecEligLoading] = useState(false);
  // CMS-1500 PDF fetch state — button shows a "Loading…" label while fetching.
  const [cms1500Loading, setCms1500Loading] = useState(false);
  // Inline CMS-1500 form preview — renders from the live form state, no server call.
  const [cms1500PreviewOpen, setCms1500PreviewOpen] = useState(false);
  // Validation drawer + result. Result shape: { errors, warnings, passed } each
  // an array of { id, field, message, vid? } — vid is the data-vid attribute
  // on the relevant form field for scroll-to-field navigation.
  const [validationOpen, setValidationOpen] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  // Gate Update / Resubmit For Approval behind a clean Validate run. formVersion
  // bumps on any meaningful form change; runValidation snapshots the version it
  // ran against — they only match while the form hasn't been touched since.
  const [formVersion, setFormVersion] = useState(0);
  const [validatedAtVersion, setValidatedAtVersion] = useState(-1);
  // Eligibility details modal — full 271 breakdown (copay, deductible, coinsurance, plan coverage).
  const [eligDetailsOpen, setEligDetailsOpen] = useState(false);
  const [eligDetails, setEligDetails] = useState(null);
  const [eligDetailsLoading, setEligDetailsLoading] = useState(false);
  const [eligDetailsLabel, setEligDetailsLabel] = useState("");
  const [patientSecInsData, setPatientSecInsData] = useState("");
  const [physicianDropdownData, setPhysicianDropdownData] = useState([]);
  const [isPrimarySkipped, setIsPrimarySkipped] = useState(false);
  const [claimNotes, setclaimNotes] = useState("");
  const [priorAuthority, setPriorAuthority] = useState("");
  const [attachmentsData, setattachmentsData] = useState([]);
  const [allDocumentData, setallDocumentData] = useState([]);
  const [showEncModal, setShowEncModal] = useState(false);
  const [docRowCheckbox, setdocRowCheckbox] = useState([]);
  const [docAllCheckbox, setdocAllCheckbox] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [selectType] = useState("");
  const [selectMethod] = useState("");
  const [searchDocDate, setSearchDocDate] = useState("");
  const [searchDocName, setSearchDocName] = useState("");
  const [searchDocType, setSearchDocType] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [successDeleteModal, setSuccessDeleteModal] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [selectedDocName, setSelectedDocName] = useState("");
  const [selectedDocForDelete, setSelectedDocForDelete] = useState("");
  const [successTimer] = useState(8000);
  const [attachmentTypeData, setattachmentTypeData] = useState([]);
  const [selectedClaimID, setSelectedClaimID] = useState("");
  const [selectedCPT, setSelectedCPT] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [claimIdSearch, setClaimIdSearch] = useState("");
  const [newClaimIdSearch, setNewClaimIdSearch] = useState("");
  const [providerNameSearch, setProviderNameSearch] = useState("");
  const [procedureSearch, setProcedureSearch] = useState("");
  const [grandTotal, setGrandTotal] = useState(0);
  const [, setSelectedCPTServiceID] = useState("");
  const [isLoading, setisLoading] = useState(false);

  const [rows, setRows] = useState([
    {
      fromDate: "",
      toDate: "",
      procedure: "",
      selectedCPTs: [],
      modDataList: [],
      diagDataList: [],
      mod1: "",
      mod2: "",
      mod3: "",
      diag1: "",
      diag2: "",
      diag3: "",
      diag4: "",
      hideList: false,
      hideMod: false,
      hideMod1: false,
      hideMod2: false,
      hideDiag: false,
      hideDiag1: false,
      hideDiag2: false,
      hideDiag3: false,
      totalCharge: "",
      Adjustment: "",
      SecPendingAmt: "",
      Deductible: "",
      PatientAmount: "",
      AdminWriteOff: "",
      ClaimCPTLineNumber: "",
      unit: "",
      unitCharge: "",
      ID: "",
    },
  ]);

  /* ─── Effects ─── */
  useEffect(() => { document.title = "New Encounter (V2)"; }, []);

  useEffect(() => {
    let t;
    if (showErrorModal || showUploadModal || successDeleteModal || showConfirmSaveModal) {
      t = setTimeout(() => {
        setShowErrorModal(false);
        setShowUploadModal(false);
        setShowDeleteModal(false);
        setSuccessDeleteModal(false);
        setShowConfirmSaveModal(false);
      }, successTimer);
    }
    return () => clearTimeout(t);
  }, [successTimer, showErrorModal, showUploadModal, successDeleteModal, showConfirmSaveModal]);

  useEffect(() => {
    if (claimDetails) {
      setclaimNotes(claimDetails.EClaimNote);
      setPriorAuthority(claimDetails.PriorAuthority);
    }
  }, [claimDetails]);

  const FullName = [claimDetails?.LastName, claimDetails?.FirstName].filter(Boolean).join(", ");
  useEffect(() => {
    setSearchPatientText(FullName);
    setIsPatientSelected(FullName.trim() !== "");
    setSearchPatientData([]);
  }, [FullName]);

  useEffect(() => {
    if (id && !hasLoaded) {
      getClaimStatusData();
      setHasLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, claimDetails]);

  // Static dropdowns (locations, NPIs, taxonomies, providers) only depend on
  // the practice — fetch once per userData change, not on every patient pick.
  useEffect(() => {
    getDropdown();
    getProvider();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  // Physician dropdown is patient-scoped; fetch only when we actually have a patient.
  useEffect(() => {
    if (selectedPatientId) getPhysicianDropdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatientId]);

  useEffect(() => {
    if (searchPatientText && searchPatientText.trim() !== "") fetchSuggestionPatientNames();
    else setSearchPatientData([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchPatientText]);

 
  
  
    useEffect(() => {
      if (selectedInsurance === "") {
  
        setSelectedInsurance("");
        setSelectedPatientInsurance("");
      }
      else if (selectedInsurance && Object.keys(selectedInsurance).length > 0) {
  
        setSelectedPatientInsurance(selectedInsurance.InsPolicyNumber || "");
      }
      else if (patientInsData?.length > 0) {
  
        const defaultInsurance = patientInsData.find(
          (ins) => ins.PatInsStatus == 1
        );
  
        if (defaultInsurance) {
          setSelectedInsurance(defaultInsurance);
          setSelectedPatientInsurance(defaultInsurance.InsPolicyNumber || "");
        }
      }
    }, [patientInsData, selectedInsurance]);
  

  useEffect(() => { if (claimRowsData?.length > 0) addClaimDetailsToRows(); /* eslint-disable-next-line */ }, [claimRowsData]);

  useEffect(() => {
    const claimDOS = formatDOSDate(claimDetails?.ClaimDOS);
    const Dos = formatAppointMentDate(claimDetails?.AppointmentDate);
    const inputJSON = { DOS: claimDOS || Dos, PatientID: claimDetails?.PatientID || claimDetails?.PatientId };
    if (!inputJSON.PatientID) return;
    const sInputJSON = JSON.stringify({ objIns: inputJSON });
    const url = `${APIConstants.PATIENT_PORTAL_URL}${CommonAPIConstants.GetPatientInsurance}?sInputJSON=${sInputJSON}`;
    fetch(url, { method: "GET", headers: { Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${sAuthToken}` } })
      .then((r) => (r.status !== 200 ? null : r.json()))
      .then((data) => {
        if (!data) return;
        const parsed = JSON.parse(data);
        const pri = parsed?.filter((i) => i?.InsType == 1);
        const sec = parsed?.filter((i) => i?.InsType == 2);
        setPatientInsData(pri);
        setPatientSecInsData(sec);
        setSelectedInsurance(pri[0]);
        if (pri[0]?.PatInsStatus == 1) setShowEligible(true);
        if (pri[0]?.PatInsStatus == 2) setShowSecondaryEligible(true);
        setselectedSecInsurance(sec[0]);
        setSelectedPatientInsurance(pri && pri[0]?.insPolicyNumber);
      })
      .catch(() => {});
  }, [claimDetails?.PatientID, claimDetails?.ClaimDOS, claimDetails, sAuthToken]);

  useEffect(() => { setSelectedBillingNPI(claimDetails?.claim32a); }, [claimDetails?.claim32a]);
  useEffect(() => { setSelectedDOB(claimDetails?.PatDOB); }, [claimDetails]);
  useEffect(() => { setSelectedCMS33a(claimDetails?.claim33a); }, [claimDetails?.claim33a]);
  useEffect(() => { setSelectedCMS33b(claimDetails?.claim33b); }, [claimDetails?.claim33b]);
  useEffect(() => { setSelectedServicelocation(claimDetails?.ServiceLocationCode); }, [claimDetails?.ServiceLocationCode]);
  useEffect(() => { setSelectedRenderingProvider(claimDetails?.RenderingProvider); }, [claimDetails?.RenderingProvider]);
  useEffect(() => { setSelectedProvidersCategory(claimDetails?.Box17Indicator || ""); }, [claimDetails?.Box17Indicator]);
  // Practice providers only need re-fetching when the provider-category
  // changes AND we don't already have them. getDropdown() loads everything on
  // mount, so this fires at most once if practiceProvider is still empty.
  useEffect(() => {
    if (!selectedProvidersCategory && practiceProvider.length === 0) getPracticeProvidersOnly();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvidersCategory]);
  useEffect(() => { setSelectedPracticeProvider(claimDetails?.SchedulingProvider); }, [claimDetails?.SchedulingProvider]);
  useEffect(() => { setSelectedResubmitCode(claimDetails?.ResubmitCode); }, [claimDetails?.ResubmitCode]);
  useEffect(() => { setSelectedResubmitCode("0"); }, []);
  useEffect(() => { setSelectedLocation(claimDetails?.PlaceofService ?? ""); }, [claimDetails?.PlaceofService]);
  useEffect(() => {
    if (claimDetails?.SupervisingProvider && claimDetails?.SupervisingProvider !== "0") setSelectedFilterProvider(claimDetails?.SupervisingProvider);
    else setSelectedFilterProvider("-1");
  }, [claimDetails?.SupervisingProvider]);
  useEffect(() => { setSelectedOriginalClaim(claimDetails?.PayerRefNumber); }, [claimDetails?.PayerRefNumber]);
  useEffect(() => {
    if (selectedLocation === "") setSelectedBillingAddress("<--Select-->");
    else {
      const lo = locations.find((l) => l.LocationId == selectedLocation);
      if (lo) setSelectedBillingAddress(lo.LocationName);
    }
  }, [selectedLocation, locations]);

  useEffect(() => { if (claimDetails?.ClaimDOS) setAppointmentDate(formatDOSDate(claimDetails.ClaimDOS)); }, [claimDetails]);

  const allZero = claimRowsData.every((i) => i.recvdCharge == 0);
  const someZero = claimRowsData.some((i) => i.recvdCharge == 0);
  const isPartiallyDenied = !allZero && someZero;
  const hasExistingClaim = id !== undefined && id !== null;

  useEffect(() => {
    if (isPartiallyDenied) {
      const m = {};
      claimRowsData.forEach((r) => { if (r.recvdCharge == 0) m[r.ID] = true; });
      setIsCheckboxChecked(m);
    }
  }, [isPartiallyDenied, claimRowsData]);

  useEffect(() => { handleGetClaimAttachments(); handleGetClaimAttachmentTypes(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (selectedClaimID > 0) handleUploadClaimAttachments(); /* eslint-disable-next-line */ }, [selectedClaimID]);
  useEffect(() => { if (claimRowsData?.length > 0) updateGrandTotal(); /* eslint-disable-next-line */ }, [claimRowsData, rows]);

  /* ─── API calls ─── */
  const getProvider = () => {
    const url = APIConstants.COMMON_BASE_URL + `CommonData/BillingProviders?sInputJSON={"PracticeID":"${userPracticeID}"}`;
    fetch(url, { method: "GET", headers: { Authorization: `Bearer ${sAuthToken}` } })
      .then((r) => r.json()).then(() => {}).catch(() => {});
  };

  const getDropdown = () => {
    const url = `${APIConstants.COMMON_BASE_URL}${IntelliBillConstants.GetIntelliDropDowns}?sInputJSON={PracticeID:${userPracticeID},UserId:${userId} }`;
    fetch(url, { method: "GET", headers: { Accept: "application/json", "Content-Type": "application/json" } })
      .then((r) => (r.status !== 200 ? null : r.json()))
      .then((data) => {
        if (!data) return;
        const parsed = JSON.parse(data);
        setLocations(JSON.parse(parsed.LocationDetails));
        setCMS33aData(JSON.parse(parsed.CMSbox33aData));
        setCMS33bData(JSON.parse(parsed.CMSbox33bData));
        setBillingNPIs(JSON.parse(parsed.PracticeBillingNPIs));
        setServiceLocations(JSON.parse(parsed.ServiceLocation));
        setProviderCategory(JSON.parse(parsed.ProvidersCategories));
        setPracticeTaxonomycodes(JSON.parse(parsed.PracticeTaxonomycodes));
        // Practice providers come from the same payload — extract here so the
        // separate getPracticeProvidersOnly() round-trip becomes unnecessary.
        if (parsed.PracticeProviders) setPracticeProvider(JSON.parse(parsed.PracticeProviders));
        setLoading(false);
      })
      .catch(() => {});
  };

  const getPracticeProvidersOnly = () => {
    const url = `${APIConstants.COMMON_BASE_URL}${IntelliBillConstants.GetIntelliDropDowns}?sInputJSON={PracticeID:${userPracticeID},UserId:${userId} }`;
    fetch(url, { method: "GET", headers: { Accept: "application/json", "Content-Type": "application/json" } })
      .then((r) => (r.status !== 200 ? null : r.json()))
      .then((data) => {
        if (!data) return;
        const parsed = JSON.parse(data);
        setPracticeProvider(JSON.parse(parsed.PracticeProviders));
      })
      .catch(() => {});
  };

  const getPhysicianDropdown = () => {
    // Backend returns 400 on empty PatientID. V1 fired this on mount before a
    // patient was selected and ate the 400 in the console — V2 skips instead.
    if (!selectedPatientId) return;
    const url = `${APIConstants.PATIENT_PORTAL_URL}${CommonAPIConstants.GetPatientRefPhysician}?PatientID=${selectedPatientId}`;
    fetch(url, { method: "GET", headers: { Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${sAuthToken}` } })
      .then((r) => (r.status !== 200 ? null : r.json()))
      .then((data) => { if (data) setPhysicianDropdownData(JSON.parse(data)); setLoading(false); })
      .catch(() => {});
  };

  const getClaimStatusData = () => {
    const objInput = {
      iPracticeID: userPracticeID + "", sFromDate: "01/01/2023", sToDate: "01/01/2023", sFilterType: "",
      sPatientID: "", sInsuranceID: "", sCPT: "", sInsorPatFilter: "claimID", sLocationID: 0, sFilterText: id,
      ViewType: 0, isClosedClaims: true, sService: [], sCPTGroups: [], sClaimStatus: "", sDefaultSearch: false,
      roleID: userData.roleID, iProviderTemplateID: userData.iProviderTemplateID, DefaultTimeZoneOffSet: userData.DefaultTimeZoneOffSet,
    };
    const myInput = JSON.stringify({ ObjClaims: objInput });
    const url = `${APIConstants.BASE_URL_BILLING}${ClaimStatusConstants.GetClaimStatusData}?sInputJSON=${myInput}`;
    fetch(url, { method: "GET", headers: { Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${sAuthToken}` } })
      .then((r) => (r.status !== 200 ? null : r.json()))
      .then((data) => {
        if (!data) return;
        const parsed = JSON.parse(data);
        const td = parsed?.Data[0];
        setClaimRowsData(td);
        setClaimDetails(td[0]);
        setBtnStatus(td[0]?.Status);
        setDenied(td[0]?.isDenied);
        setSelectedCPTServiceID(td[0]?.CPTServiceID);
        setSelectedPatientID(td[0].PatientID);
      })
      .catch(() => { setError("API request failed"); setLoading(false); });
  };

  const fetchSuggestionPatientNames = () => {
    const inputJSON = { PracticeID: userPracticeID, SearchKey: searchPatientText, SearchBy: 0, isFromLogin: 0 };
    const sJSONInput = JSON.stringify(inputJSON);
    const url = `${APIConstants.BASE_URL_SEARCH}${CommonAPIConstants.GetAllPatientNames}?sJSONInput=${sJSONInput}`;
    fetch(url, { method: "GET", headers: { Accept: "application/json", "Content-Type": "application/json" } })
      .then((r) => (r.status !== 200 ? null : r.json()))
      .then((data) => {
        if (!data) return;
        const parsed = JSON.parse(data);
        const arr = parsed.Data;
        if (Array.isArray(arr)) setSearchPatientData(arr.map((i) => ({ label: `${i.LastName}, ${i.FirstName}`, val: i.PatientID, dob: i.DOB })));
      })
      .catch(() => {});
  };

  const handlePatientSuggestion = (s) => {
    const d = new Date(s.dob);
    setSearchPatientText(s.label);
    setSelectedDOB(d.toLocaleDateString());
    setIsPatientSelected(true);
    setSelectedPatientID(s?.val);
    const url = `${APIConstants.PATIENT_PORTAL_URL}${CommonAPIConstants.GetPatInsuranceDetails}?PatientId=${s.val}`;
    fetch(url, { method: "GET", headers: { Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${sAuthToken}` } })
      .then((r) => (r.status !== 200 ? null : r.json()))
      .then((data) => {
        if (!data) return;
        const parsed = JSON.parse(data);
        console.log("Fetched insurance details for selected patient:", parsed);
        const pri = parsed?.filter((i) => i?.InsType == 1);
        const sec = parsed?.filter((i) => i?.InsType == 2);
        setPatientInsData(pri);
        setPatientSecInsData(sec);
        setSelectedInsurance(pri[0]);
        if (pri[0]?.PatInsStatus == 1) setShowEligible(true);
        if (pri[0]?.PatInsStatus == 2) setShowSecondaryEligible(true);
        setselectedSecInsurance(sec[0]);
        setSelectedPatientInsurance(pri && pri[0]?.InsPolicyNumber);
      })
      .catch(() => {});
  };

  const fetchSearchCPTListByRow = (index, searchKey) => {
    const inputJSON = { PracticeID: userPracticeID, SearchKey: searchKey };
    const sJSONInput = JSON.stringify(inputJSON);
    const url = `${APIConstants.BASE_URL_SEARCH}${CommonAPIConstants.GetAllCPTList}?sInputJSON=${sJSONInput}`;
    fetch(url, { method: "GET", headers: { Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${sAuthToken}` } })
      .then((r) => r.json()).then((data) => {
        const cptData = JSON.parse(data);
        const updated = [...rows];
        updated[index].selectedCPTs = cptData?.filter((it) => `${it.CPTCode} ${it.CPTDesc}` !== searchKey);
        setRows(updated);
      }).catch(() => {});
  };

  const fetchAllMacroData = async (macroName) => {
    try {
      const inputJSON = { PracticeID: userPracticeID, MacroName: macroName };
      const url = `${APIConstants.BASE_URL_BILLING}${CommonAPIConstants.GetAllMacroDetails}?sInputJSON=${JSON.stringify(inputJSON)}`;
      const response = await fetch(url, { method: "GET", headers: { Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${sAuthToken}` } });
      if (!response.ok) return null;
      const data = await response.json();
      return JSON.parse(data);
    } catch { return null; }
  };

  const fetchModList = (value, index) => {
    const inputStruct = { modName: value };
    const myInput = { sInputJSON: JSON.stringify(inputStruct) };
    fetch(APIConstants.BASE_URL + CommonAPIConstants.GetModList, {
      method: "POST", headers: { Accept: "application/json", "Content-Type": "application/json" }, body: JSON.stringify(myInput),
    })
      .then((r) => r.json()).then((data) => {
        const parsed = JSON.parse(data.d);
        const modData = parsed.Data;
        if (Array.isArray(modData)) {
          const updated = [...rows];
          updated[index].modDataList = modData.map((it) => (it && typeof it === "object" && "Value" in it ? { val: it.Value.split(" | ")[0] } : null));
          setRows(updated);
        }
      }).catch(() => {});
  };

  const fetchDiagListByRow = (val, index) => {
    const inputStruct = { icdName: val };
    const myInput = { sInputJSON: JSON.stringify(inputStruct) };
    fetch(APIConstants.BASE_URL + CommonAPIConstants.GetIcdList, {
      method: "POST", headers: { Accept: "application/json", "Content-Type": "application/json" }, body: JSON.stringify(myInput),
    })
      .then((r) => r.json()).then((data) => {
        const parsed = JSON.parse(data.d);
        const updated = [...rows];
        updated[index].diagDataList = parsed.Data;
        setRows(updated);
      }).catch(() => {});
  };

  /* Body format mirrors the working PrimaryInsurance.js call (no InsCompanyID,
     includes UserID). On success the payer's InsStatus / EligibilityError
     fields are surfaced inline so the user sees Active vs Inactive vs the
     specific reason for failure — not just a generic green check. */
  const checkEligibility = (ins, kind = "secondary") => {
    if (!ins) return;
    const policy = ins?.InsPolicyNumber ?? ins?.insPolicyNumber;
    if (!policy) return;
    const setLoading = kind === "primary" ? setPriEligLoading : setSecEligLoading;
    const setResult  = kind === "primary" ? setPriEligibility : setSecEligibility;
    const setLegacy  = kind === "primary" ? setShowEligible   : setShowSecondaryEligible;

    setLoading(true);
    setResult(null);

    const body = {
      objPatientDetails: {
        PatientID: selectedPatientId || claimDetails?.PatientID,
        PracticeID: userPracticeID,
        PolicyNumber: policy,
        DOS: appointmentDate ? appointmentDate : formatDOSDate(new Date()),
        RowIndex: 0,
        UserID: userData?.userID ?? 0,
      },
    };
    const url = `${APIConstants.PATIENT_PORTAL_URL}${CommonAPIConstants.GetPatientEligibility}?sInputJSON=${JSON.stringify(body)}`;
    fetch(url, {
      method: "GET",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
    })
      .then((r) => (r.status !== 200 ? null : r.json()))
      .then((raw) => {
        setLoading(false);
        if (!raw) { setResult({ status: "Error", error: "Eligibility service unavailable" }); return; }
        // Backend double-encodes — first parse to string, then to object.
        let parsed = raw;
        try { parsed = typeof raw === "string" ? JSON.parse(raw) : raw; } catch { /* already an object */ }
        const status = parsed?.InsStatus ?? parsed?.Status ?? "";
        const error  = parsed?.EligibilityError ?? parsed?.Error ?? "";
        setResult({ status, error });
        if (status && status.toLowerCase() === "active" && !error) setLegacy(true);
      })
      .catch(() => { setLoading(false); setResult({ status: "Error", error: "Network error" }); });
  };

  const handlePrimaryIns = () => { if (selectedInsurance) checkEligibility(selectedInsurance, "primary"); };

  /* Fetch the full 271 breakdown for an insurance (separate endpoint from
     the basic Check). Opens a modal with payer, copay, deductible,
     coinsurance, and plan coverage details — same shape as V1's
     PatientEligibilityDetails screen. */
  const handleViewEligibilityDetails = (ins, label) => {
    if (!ins) return;
    const policy  = ins?.InsPolicyNumber ?? ins?.insPolicyNumber;
    const insCoID = ins?.InsCoID         ?? ins?.insCoID;
    if (!policy || !insCoID) return;
    setEligDetailsOpen(true);
    setEligDetailsLoading(true);
    setEligDetails(null);
    setEligDetailsLabel(label || "");
    const body = {
      objPatientDetails: {
        PatientID: selectedPatientId || claimDetails?.PatientID,
        InsCompanyID: insCoID,
        PolicyNumber: policy,
        UserID: userData?.userID ?? 0,
        DOS: appointmentDate ? appointmentDate : formatDOSDate(new Date()),
      },
    };
    const url = `${APIConstants.PATIENT_PORTAL_URL}${CommonAPIConstants.PatientEligibilityDetails}?sInputJSON=${JSON.stringify(body)}`;
    fetch(url, {
      method: "GET",
      headers: { Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${sAuthToken}` },
    })
      .then((r) => (r.status !== 200 ? null : r.json()))
      .then((raw) => {
        setEligDetailsLoading(false);
        if (!raw) { setEligDetails({ error: "Service unavailable" }); return; }
        let parsed = raw;
        try { parsed = typeof raw === "string" ? JSON.parse(raw) : raw; } catch { /* already object */ }
        setEligDetails(parsed);
      })
      .catch(() => { setEligDetailsLoading(false); setEligDetails({ error: "Network error" }); });
  };

  /* ─── Row state helpers ─── */
  const handleCptChange = (index, value) => {
    const updated = [...rows];
    updated[index].procedure = value;
    updated[index].hideList = value.trim() !== "";
    setRows(updated);
    if (value.trim() !== "") fetchSearchCPTListByRow(index, value);
  };

  const saveCpt = async (index, value) => {
    const updated = [...rows];
    if (value?.GrpName !== "macro") {
      updated[index].procedure = value?.CPTCode;
      updated[index].unit = 1;
      updated[index].unitCharge = value?.UnitCharge;
      const tot = 1 * parseFloat(value?.UnitCharge || 0);
      updated[index].totalCharge = parseFloat(tot).toFixed(2);
      updated[index].hideList = false;
      setSelectedCPTServiceID(value?.CPTServiceID);
    } else {
      const response = await fetchAllMacroData(value?.CPTCode);
      response?.forEach((el, i) => {
        if (!updated[index + i]) updated[index + i] = { ...updated[index] };
        updated[index + i].procedure = el?.CPT;
        updated[index + i].unit = el?.Units;
        updated[index + i].totalCharge = el?.TotalCharge;
        updated[index + i].unitCharge = el?.UnitCharge && Number(el?.UnitCharge);
        updated[index + i].hideList = false;
        updated[index + i].mod1 = el?.Mod1;
        updated[index + i].mod2 = el?.Mod2;
        updated[index + i].mod3 = el?.Mod3;
        updated[index + i].diag1 = el?.Diag1;
        updated[index + i].diag2 = el?.Diag2;
        updated[index + i].diag3 = el?.Diag3;
        updated[index + i].diag4 = el?.Diag4;
      });
    }
    setRows(updated);
    updateGrandTotal();
  };

  const saveTableData = (index, key, value, hide) => {
    const updated = [...rows];
    updated[index][key] = value;
    if (hide) updated[index][hide] = false;
    setRows(updated);
  };

  const handleDiagChange = (value, key, index, hide) => {
    const updated = [...rows];
    updated[index][key] = value;
    updated[index][hide] = value.trim() !== "";
    setRows(updated);
    if (value.trim() !== "") fetchDiagListByRow(value, index);
  };

  const handleModChange = (value, key, index, hide) => {
    const updated = [...rows];
    updated[index][key] = value;
    updated[index][hide] = true;
    if (value === "") { updated[index][key] = ""; updated[index][hide] = false; }
    setRows(updated);
    if (value !== "") fetchModList(value, index);
  };

  const handleCheckboxClick = (rowId) => setIsCheckboxChecked((p) => ({ ...p, [rowId]: !p?.[rowId] }));

  const handleAddRow = () => {
    setShowPaidErrorModal(false);
    setRows([
      ...rows,
      {
        fromDate: appointmentDate || "", toDate: appointmentDate || "", procedure: "",
        selectedCPTs: [], modDataList: [], diagDataList: [],
        mod1: "", mod2: "", mod3: "", diag1: "", diag2: "", diag3: "", diag4: "",
        hideList: false, hideMod: false, hideMod1: false, hideMod2: false,
        hideDiag: false, hideDiag1: false, hideDiag2: false, hideDiag3: false,
        totalCharge: "", Adjustment: "", SecPendingAmt: "", Deductible: "", PatientAmount: "",
        AdminWriteOff: "", ClaimCPTLineNumber: "", unit: "", unitCharge: "",
      },
    ]);
  };

  const checkThenAddRow = (row) => {
    if (claimDetails.ClaimStatusInt == 1) { setSelectedRow(row); setShowPaidErrorModal(true); }
    else handleAddRow();
  };

  const handleRemoveRow = (i, row) => {
    setShowConfirmDeleteModal(true);
    setSelectedIndex(i);
    setSelectedCPT(row.procedure);
  };

  const DeleteClaimCPT = async () => {
    try {
      const inputObj = { objClaims: { ClaimID: Number(id), CPT: selectedCPT, PracticeID: userPracticeID } };
      const sInputJSON = JSON.stringify(inputObj);
      const r = await fetch(`${APIConstants.BASE_URL_BILLING}${NewEncounterConstants.DeleteClaimCPT}?sInputJSON=${sInputJSON}`,
        { method: "DELETE", headers: { Accept: "application/json", "Content-Type": "application/json" } });
      if (r.status === 200) {
        setRows((prev) => prev.filter((_, idx) => idx !== selectedIndex));
        setShowConfirmDeleteModal(false);
        setShowConfirmSaveModal(true);
      }
    } catch (e) { console.error(e); }
  };

  const handleSecIns = () => { if (selectedSecInsurance) checkEligibility(selectedSecInsurance, "secondary"); };
  const handleSkipPrimary = () => setIsPrimarySkipped(true);

  const handlePracticeButtonClick = (item) => {
    setSelectedCMS33a(item.NPI);
    setSelectedBillingNPI(item.NPI);
    const m = practiceTaxonomycodes.find((t) => t.PracticeLabel === item.PracticeLabel);
    if (m) setSelectedCMS33b(m.Taxonomycode);
  };

  const handleSelectChange = (e) => { setSelectedServicelocation(e.target.value); };
  const handleSelectBillingNPI = (e) => setSelectedBillingNPI(e.target.value);
  const handleSelectFilterProviders = (e) => setSelectedFilterProvider(e.target.value);

  const handleInsuranceChange = (e) => {
    if (e.target.value === "-1") { setSelectedInsurance(""); return; }
    patientInsData.find((ins) => {
      if (ins.insCoID === e.target.value || ins.InsCoID === e.target.value) {
        setSelectedPatientInsurance(ins?.InsPolicyNumber);
        setSelectedInsurance(ins);
        if (ins?.PatInsStatus === 1) setShowEligible(true);
        if (ins?.PatInsStatus === 2) setShowSecondaryEligible(true);
      }
      return ins.insCoID === e.target.value || ins.InsCoID === e.target.value;
    });
  };

  const handleSecInsuranceChange = (e) => {
    if (e.target.value === "-1") { setselectedSecInsurance(""); return; }
    const found = patientSecInsData.find((i) => i.insCoID === e.target.value || i.InsCoID === e.target.value || i.InsCoName === e.target.value);
    if (found) setselectedSecInsurance(found);
  };

  const handleFromDateChange = (date) => { if (date) setStartDate(format(date, "MM/dd/yyyy")); else setStartDate(null); };
  const handleToDateChange = (date) => { if (date) setEndDate(format(date, "MM/dd/yyyy")); else setEndDate(null); };
  const handleToApproveDateChange = (date) => { if (date) setApproveDate(format(date, "MM/dd/yyyy")); else setApproveDate(null); };

  const handleToAppointmentDateChange = async (date) => {
    if (date) {
      const formatted = format(date, "MM/dd/yyyy");
      const dt = new Date(date);
      const updated = await patientInsData?.filter((i) => {
        const e = new Date(i.endDate || i.EndDate);
        const s = new Date(i.startDate || i.StartDate);
        return dt >= s && dt <= e;
      });
      if (updated?.length) { setPatientInsData(updated); setSelectedInsurance(updated[0]); }
      const cur = formatDOSDate(new Date());
      setAppointmentDate(formatted); setApproveDate(cur); setStartDate(formatted); setEndDate(formatted);
      setFromDate(formatted); setToDate(formatted);
      setFromDates((p) => p.map(() => formatted));
      setToDates((p) => p.map(() => formatted));
      setRows((p) => p.map((r) => ({ ...r, fromDate: formatted, toDate: formatted })));
    } else setAppointmentDate(null);
  };

  const updateGrandTotal = () => {
    const total = rows.reduce((sum, r) => (r.IsCPTMoved !== 1 ? sum + (parseFloat(r.totalCharge) || 0) : sum), 0);
    setGrandTotal(total.toFixed(2));
  };

  const formatInsDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return "";
    const y = dt.getFullYear(), m = dt.getMonth() + 1, day = dt.getDate();
    if (y === 1900 && m === 1 && day === 1) return "";
    return dt.toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" });
  };

  const addClaimDetailsToRows = () => {
    const newRows = claimRowsData.map((d) => ({
      fromDate: formatDOSDate(d?.DOS), toDate: formatDOSDate(d?.DOS),
      ClaimId: d?.ClaimId, providername: d?.providername,
      procedure: d.ClaimCPT, mod1: d.Mod1, mod2: d.Mod2, mod3: d.Mod3,
      diag1: d.Diag1, diag2: d.Diag2, diag3: d.Diag3, diag4: d.Diag4,
      unit: d?.Units, unitCharge: d?.UnitCharge, totalCharge: d?.totalcharge,
      recvdCharge: d?.recvdCharge, SecrecvdCharge: d?.SecrecvdCharge,
      Adjustment: d?.Adjustment, SecPendingAmt: d?.SecPendingAmt,
      Deductible: d?.Deductible, PatientAmount: d?.PatientAmount,
      AdminWriteOff: d?.AdminWriteOff, ClaimCPTLineNumber: d?.ClaimCPTLineNumber,
      FederalReduction: d?.FederalReduction, CPTServiceID: d?.CPTServiceID,
      ID: d?.ID, IsCPTMoved: d?.IsCPTMoved,
    }));
    setRows(newRows);
  };

  const filteredRows = rows?.filter((row) => (
    (claimIdSearch === "" || row?.ClaimId?.toString().includes(claimIdSearch)) &&
    (newClaimIdSearch === "" || row?.NewClaimId?.toString().includes(newClaimIdSearch)) &&
    (providerNameSearch === "" || row?.providername?.toLowerCase().includes(providerNameSearch?.toLowerCase())) &&
    (procedureSearch === "" || row?.procedure?.toLowerCase().includes(procedureSearch?.toLowerCase()))
  ));

  const formatStartDates = fromDates ? fromDates.map(convertDateFormat) : [new Date().toLocaleDateString("en-US")];
  const formatToDates = toDates ? toDates.map(convertDateFormat) : [new Date().toLocaleDateString("en-US")];
  const filteredProviders = practiceProvider?.filter((p) => p.Supervisor === "1" || p.Supervisor === "3");

  const validateDocuments = () => {
    for (let i = 0; i < selectedDocs.length; i++) {
      if (!selectedDocs[i].selectType || !selectedDocs[i].selectMethod) { setShowValidationModal(true); return false; }
    }
    return true;
  };

  const handleCloseModals = () => {
    setErrorMessage("");
    setShowPaidErrorModal(false);
    setShowSuccessModal(false);
    setShowEncModal(false);
    setShowErrorModal(false);
    setShowUploadModal(false);
    setShowDeleteModal(false);
    setSuccessDeleteModal(false);
    setShowValidationModal(false);
    setShowConfirmDeleteModal(false);
    setShowConfirmSaveModal(false);
  };

  /* ─── Save flows (verbatim from V1, just lifted) ─── */
  const GenerateNewClaims = async () => {
    if (!validateDocuments()) return;
    let err = "";
    if (!isCheckboxChecked && id) err += "Please select the one checkbox.\n";
    if (selectedInsurance === "-1" || selectedInsurance <= 0 || selectedInsurance === null) err += "Please select primary insurance\n";
    if (!selectedBillingNPI || selectedBillingNPI <= 0) err += "Please select service location NPI.";
    if (!selectedCMS33a || selectedCMS33a <= 0) err += "Please billing provider NPI.\n";
    if (!selectedCMS33b || selectedCMS33b <= 0) err += "Please select provider taxonomy code.\n";
    if (!selectedRenderingProvider || selectedRenderingProvider <= 0) err += "Please select rendering provider.\n";
    if (!selectedLocation || selectedLocation <= 0) err += "Please select place of service.\n";
    if (!selectedServiceLocation || selectedServiceLocation <= 0) err += "Please select ServiceLocation 24B.\n";
    if (!appointmentDate) err += "Please select Appointment Date.";
    if (err) { setErrorMessage(err); return; }
    setErrorMessage("");

    const formatAppointDate = appointmentDate ? convertDateFormat(appointmentDate) : new Date().toLocaleDateString("en-US");
    let filtered = isCheckboxChecked ? rows.filter((r) => isCheckboxChecked[r.ID]) : rows;
    let hasDiag = false;
    for (const r of filtered) if (r?.diag1 || r?.diag2 || r?.diag3 || r?.diag4) { hasDiag = true; break; }
    if (!hasDiag) { setErrorMessage("Add at least 1 Diag value.\n"); return; }

    const ClaimLineItems = filtered.map((r) => ({
      CPTcode: r?.procedure, Modifier: r?.mod1, Modifier2: r?.mod2, Modifier3: r?.mod3,
      ICD1: r?.diag1?.split(" - ")[0] || "", ICD2: r?.diag2?.split(" - ")[0] || "",
      ICD3: r?.diag3?.split(" - ")[0] || "", ICD4: r?.diag4?.split(" - ")[0] || "",
      Unit: r?.unit, UnitCharges: r?.unitCharge, CPTServiceID: r?.CPTServiceID ?? "",
      Claim24B: selectedServiceLocation !== undefined ? selectedServiceLocation : "",
    }));
    const curDOS = formatDOSDate(claimDetails?.ClaimDOS);
    let SupervisorProviderfornewclaim = selectedFilterProvider !== undefined ? selectedFilterProvider : "0";
    if (SupervisorProviderfornewclaim === "") SupervisorProviderfornewclaim = "0";

    const objClaim = {
      PracticeID: userData.practiceID || "",
      PatientID: claimDetails.PatientID || selectedInsurance.PatientID,
      DOS: appointmentDate ?? curDOS ?? "",
      ProviderID: selectedRenderingProvider || "",
      SchedulingProviderID: selectedPracticeProvider,
      SupervisorIndicator: selectedProvidersCategory,
      SupervisorProviderID: SupervisorProviderfornewclaim,
      Insurance: selectedInsurance.InsCoID ?? selectedInsurance.insCoID,
      PolicyNumber: selectedInsurance.insPolicyNumber ?? selectedInsurance.InsPolicyNumber,
      Claim24B: selectedServiceLocation !== undefined ? selectedServiceLocation : "",
      Claim32A: selectedBillingNPI || "", Claim33A: selectedCMS33a || "", Claim33B: selectedCMS33b || "",
      PlaceofService: selectedLocation || "",
      SkipBox32: isBOX32checked,
      UserID: userData.userID, RoleID: userData.roleID,
      IsClaimSplit: false, IsScheduleAppointment: true, IsPrimarySkipped: isPrimarySkipped,
      PatientSecInsurance: selectedSecInsurance?.insCoID ?? selectedSecInsurance?.InsCoID,
      PatientSecInsurancePolicy: selectedSecInsurance?.insPolicyNumber ?? selectedSecInsurance?.InsPolicyNumber,
      PageModule: "Newenc", ClaimENote: claimNotes ?? "", objCPTDetails: ClaimLineItems,
    };
    const myInputData = JSON.stringify(JSON.stringify({ objClaim: [objClaim] }));
    try {
      const r = await fetch(APIConstants.BASE_URL_BILLING + NewEncounterConstants.GenerateNewClaim, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${sAuthToken}` },
        body: myInputData,
      });
      const d = await r.json();
      setisLoading(false);
      if (d > 0) { setSelectedClaimID(d); setShowSuccessModal(true); window.open(`/#/Claimapproval`, "_blank"); }
    } catch { setisLoading(false); }
  };

  const SaveSecondaryClaim = () => {
    if (!selectedSecInsurance || selectedSecInsurance == -1 || selectedSecInsurance == 0) { setErrorMessage("Please select secondary insurance"); return; }
    const ClaimLineItems = filteredRows.map((r) => ({
      CPTCode: r?.procedure, Modifier: r?.mod1, Modifier2: r?.mod2, Modifier3: r?.mod3,
      ICD1: r?.diag1?.split(" - ")[0] || "", ICD2: r?.diag2?.split(" - ")[0] || "",
      ICD3: r?.diag3?.split(" - ")[0] || "", ICD4: r?.diag4?.split(" - ")[0] || "",
      Claim24B: selectedServiceLocation !== undefined ? selectedServiceLocation : "",
    }));
    const curDOS = formatDOSDate(claimDetails?.ClaimDOS);
    let SupervisorProviderfornewclaim = selectedFilterProvider !== undefined ? selectedFilterProvider : "0";
    if (SupervisorProviderfornewclaim === "") SupervisorProviderfornewclaim = "0";
    const objClaim = {
      ClaimID: id,
      PatientSecInsurance: selectedSecInsurance?.InsCoID, PatientSecInsurancePolicy: selectedSecInsurance?.InsPolicyNumber,
      DOS: appointmentDate ?? curDOS ?? "", PatientID: claimDetails?.PatientID ?? "",
      PracticeID: userPracticeID, UpdatedBy: claimDetails?.updatedby,
      Claim32A: selectedBillingNPI || "", Claim33A: selectedCMS33a || "", Claim33B: selectedCMS33b || "",
      SupervisorIndicator: selectedProvidersCategory, SupervisorProviderID: SupervisorProviderfornewclaim,
      ProviderID: selectedRenderingProvider || "",
      Claim24B: selectedServiceLocation !== undefined ? selectedServiceLocation : "",
      ClaimENote: claimNotes ?? "", PriorAuthority: priorAuthority ?? "",
      objCPTDetails: ClaimLineItems,
    };
    fetch(APIConstants.BASE_URL_BILLING + NewEncounterConstants.SaveSecondaryClaim, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${sAuthToken}` },
      body: JSON.stringify(JSON.stringify({ objClaim })),
    }).then((r) => { if (r.status === 200) { setShowSuccessModal(true); window.open(`/#/Claimapproval`, "_blank"); } }).catch(() => {});
  };

  const UpdateClaimDetails = () => {
    const formatAppointDate = appointmentDate ? convertDateFormat(appointmentDate) : new Date().toLocaleDateString("en-US");
    const skipBox32 = 0;
    let err = "";
    if (!selectedBillingNPI || selectedBillingNPI <= 0) err += "Please select service location NPI.";
    if (!selectedLocation || selectedLocation <= 0) err += "Please select service location Address.";
    if (!selectedCMS33a || selectedCMS33a <= 0) err += "Please billing provider NPI.\n";
    if (!selectedCMS33b || selectedCMS33b <= 0) err += "Please select billing provider taxonomy code.";
    if (!selectedRenderingProvider || selectedRenderingProvider <= 0) err += "Please select rendering provider.";
    if (!selectedPracticeProvider || selectedPracticeProvider <= 0) err += "Please select scheduling Provider.";
    setErrorMessage(err);
    if (err) return;

    const arrClaimDetails = rows.map((r, i) => ({
      PatientId: claimDetails?.PatientID,
      From: formatStartDates[i] !== undefined ? formatStartDates[i] + "" : "",
      To: formatToDates[i] !== undefined ? formatToDates[i] + "" : "",
      CPTCode: r?.procedure, Modifier: r?.mod1, Modifier2: r?.mod2, Modifier3: r?.mod3,
      ICD1: r?.diag1, ICD2: r?.diag2, ICD3: r?.diag3, ICD4: r?.diag4,
      Unit: r?.unit, UnitCharges: r?.unitCharge, CPTLineNumber: r?.ClaimCPTLineNumber,
      CPTServiceID: r?.CPTServiceID ?? "", RowID: r?.ID,
      Claim24B: selectedServiceLocation !== undefined ? selectedServiceLocation : "",
    }));
    const objClaim = {
      PracticeID: userData.practiceID, PatientID: claimDetails?.PatientID,
      ProviderID: selectedRenderingProvider.toString(), DOS: formatAppointDate,
      SupervisorProviderID: selectedFilterProvider,
      SupervisorIndicator: selectedProvidersCategory != null && selectedProvidersCategory !== "" ? selectedProvidersCategory : "-1",
      SkipBox32: skipBox32, Insurance: claimDetails?.InsuID,
      PolicyNumber: patientInsData[0]?.InsPolicyNumber,
      Claim24B: selectedServiceLocation !== undefined ? selectedServiceLocation : "",
      Claim32A: selectedBillingNPI, Claim33A: selectedCMS33a, Claim33B: selectedCMS33b,
      PlaceofService: selectedLocation,
      UserID: userData.userID, RoleID: userData.roleID,
      ClaimID: id.toString(), IsClaimSplit: 0, SchedulingProviderID: selectedPracticeProvider,
      TimeZoneOffSet: userData.TimeZoneOffSet,
      ResubmitCode: selectedResubmitCode != null ? selectedResubmitCode : "0",
      PayerClaimRefNumber: selectedOriginalClaim,
      IsPrimarySkipped: isPrimarySkipped,
      PatientSecInsurance: selectedSecInsurance?.insCoID ?? selectedSecInsurance?.InsCoID,
      PatientSecInsurancePolicy: selectedSecInsurance?.insPolicyNumber ?? selectedSecInsurance?.InsPolicyNumber,
      ClaimENote: claimNotes ?? "", PriorAuthority: priorAuthority ?? "",
      objCPTDetails: arrClaimDetails,
    };
    const url = `${APIConstants.BASE_URL_BILLING}${NewEncounterConstants.UpdateClaimDetails}`;
    fetch(url, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${sAuthToken}` },
      body: JSON.stringify(JSON.stringify({ objClaim })),
    }).then((r) => r.json()).then((d) => {
      if (d > 0) { setShowSuccessModal(true); window.open(`/#/Claimapproval`, "_blank"); handleUploadClaimAttachments(); }
    }).catch(() => {});
  };

  /* Mirrors V1 AddNewCPTtoClaims — invoked from UpdateBtnclick when the
     claim is already paid (Status=="1" && isDenied=="0"). Ported as-is
     including V1's known quirks (empty CPT row objects, DOS = function
     reference) so behaviour stays identical. */
  const AddNewCPTtoClaims = () => {
    if (errorMessage !== "") return;
    const arrClaimNewCPTDetails = rows?.map(() => ({})) ?? [];
    const arrClaimOldCPTDetails = [];
    const objClaim = {
      ClaimID: id,
      UpdatedBy: userData.userID,
      DOS: formatDOSDate,
    };
    const inputStruct = {
      objClaim,
      arrClaimNewCPTDetails,
      arrClaimOldCPTDetails,
      LoggedInUserDetails: userData,
    };
    const myInput = JSON.stringify({ sInputJSON: JSON.stringify(inputStruct) });
    fetch(APIConstants.BASE_URL + NewEncounterConstants.AddNewCPTtoClaims, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: myInput,
    })
      .then((r) => r.json())
      .then((data) => {
        const responsedData = JSON.parse(data.d);
        if (responsedData.Status === "Success") {
          setShowSuccessModal(true);
          window.open(`/#/Claimapproval`, "_blank");
        }
      })
      .catch(() => { setError("API request failed"); setLoading(false); });
  };

  const UpdateBtnclick = () => {
    if (!validateDocuments()) return;

    if (claimDetails.Status === "1" && claimDetails.isDenied === "0") {
      AddNewCPTtoClaims();
    } else if (claimDetails.isDenied == 1) {
      // Denied-claim resubmit gate (V1 parity). When the primary insurance
      // hasn't changed since the original claim, the resubmission code and
      // original claim # are required before we let the update proceed.
      const PatientPrimaryinsuranceID = claimDetails?.InsuID;
      const iClaimPriIns = patientInsData[0]?.insCoID;
      let sResubmitablecode = selectedResubmitCode;
      if (iClaimPriIns != PatientPrimaryinsuranceID) {
        sResubmitablecode = "0";
      } else {
        if (sResubmitablecode == 0 || sResubmitablecode == "") {
          setErrorMessage("Resubmission code is invalid");
          return;
        }
        if (selectedOriginalClaim === "") {
          setErrorMessage("Original claim number is invalid");
          return;
        }
      }
    }

    UpdateClaimDetails();
  };

  const CorrectPartiallyDeniedClaim = async () => {
    const formatAppointDate = appointmentDate ? convertDateFormat(appointmentDate) : new Date().toLocaleDateString("en-US");
    let err = "";
    if (!selectedBillingNPI) err += "Please select service location NPI.\n";
    if (!selectedLocation) err += "Please select service location Address.\n";
    if (!selectedCMS33a) err += "Please select billing provider NPI.\n";
    if (!selectedCMS33b) err += "Please select billing provider taxonomy code.\n";
    if (!selectedRenderingProvider) err += "Please select rendering provider.\n";
    if (!selectedPracticeProvider) err += "Please select scheduling Provider.\n";
    setErrorMessage(err);
    if (err) return;
    const filtered = rows.filter((r) => r.recvdCharge == null || parseFloat(r.recvdCharge) === 0);
    const arr = filtered.map((r, i) => ({
      PatientId: claimDetails?.PatientID,
      From: formatStartDates[i] ? formatStartDates[i] + "" : "",
      To: formatToDates[i] ? formatToDates[i] + "" : "",
      CPTCode: r?.procedure, Modifier: r?.mod1, Modifier2: r?.mod2, Modifier3: r?.mod3,
      ICD1: r?.diag1, ICD2: r?.diag2, ICD3: r?.diag3, ICD4: r?.diag4,
      Unit: r?.unit, UnitCharges: r?.unitCharge,
      CPTLineNumber: r?.ClaimCPTLineNumber, CPTServiceID: r?.CPTServiceID ?? "", RowID: r?.ID,
    }));
    const obj = {
      PracticeID: userData.practiceID, PatientID: claimDetails?.PatientID,
      DOS: formatAppointDate, PrimaryInsurance: claimDetails?.InsuID,
      ClaimID: id.toString(), ResubmitCode: selectedResubmitCode != null ? selectedResubmitCode : "0",
      PayerClaimRefNumber: selectedOriginalClaim, IsPrimarySkipped: isPrimarySkipped,
      ClaimENote: claimNotes ?? "", PriorAuthority: priorAuthority ?? "", PriorSec: 1,
    };
    try {
      const r = await fetch(`${APIConstants.BASE_URL_BILLING}${NewEncounterConstants.CorrectPartiallyDenied}`, {
        method: "PATCH",
        headers: { Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${sAuthToken}` },
        body: JSON.stringify(JSON.stringify({ objClaim: obj, arrClaimDetails: arr })),
      });
      const d = await r.json();
      if (d > 0) { setShowSuccessModal(true); window.open("/#/Claimapproval", "_blank"); }
    } catch {}
  };

  /* ─── Attachments flow ─── */
  const handleGetClaimAttachments = async () => {
    if (!id && !selectedClaimID) { setattachmentsData([]); return; }
    const sInputJSON = JSON.stringify({ ClaimID: id || selectedClaimID, PracticeID: userData?.practiceID });
    const url = `${APIConstants.BASE_URL_BILLING}${NewEncounterConstants.GetClaimAttachments}?sInputJSON=${sInputJSON}`;
    fetch(url, { method: "GET", headers: { Accept: "application/json", "Content-Type": "application/json" } })
      .then((r) => (r.status !== 200 ? null : r.json()))
      .then((data) => { if (data) setattachmentsData(data); }).catch(() => {});
  };

  const handleGetPatientAllDocumentDetails = async () => {
    setdocAllCheckbox([]); setdocRowCheckbox([]);
    const sInputJSON = JSON.stringify({ PracticeId: userData.practiceID, PatientID: claimDetails?.PatientID || selectedPatientId });
    const url = `${APIConstants.PATIENT_PORTAL_URL}${NewEncounterConstants.GetPatientAllDocumentDetails}?sInputJSON=${sInputJSON}`;
    fetch(url, { method: "GET", headers: { Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${sAuthToken}` } })
      .then((r) => (r.status !== 200 ? null : r.json()))
      .then((data) => { if (data) { const parsed = JSON.parse(data); setallDocumentData(parsed); if (parsed) setShowEncModal(true); } }).catch(() => {});
  };

  const GetDOCPDF = (doc) => { window.open(doc, "_blank"); };

  const handleSelectDocAllChange = () => {
    const allSelected = allDocumentData.every((_, i) => docRowCheckbox.includes(i));
    if (allSelected) { setdocAllCheckbox([]); setdocRowCheckbox([]); }
    else {
      setdocAllCheckbox(Array.from({ length: allDocumentData.length }, (_, i) => i));
      setdocRowCheckbox(Array.from({ length: allDocumentData.length }, (_, i) => i));
    }
  };
  const handleDocRowCheckbox = (i) => {
    if (docAllCheckbox.includes(i)) {
      setdocAllCheckbox(docAllCheckbox.filter((s) => s !== i));
      setdocRowCheckbox((p) => p.filter((s) => s !== i));
    } else { setdocAllCheckbox([...docAllCheckbox, i]); setdocRowCheckbox((p) => [...p, i]); }
  };
  const handleSelectDocuments = () => {
    const sel = docAllCheckbox.map((i) => allDocumentData[i]);
    const docs = sel.map((d) => ({
      fileName: d.DocumentName, fileContent: "", PWK01: "", PWK02: "",
      PhyPath: d.DocPhyPath, WebPath: d.DocWebPath, Source: "Existing",
      Documentid: d.Documentid, selectType: "", selectMethod: "EL",
    }));
    setSelectedDocs((p) => [...p, ...docs]);
    setShowEncModal(false);
  };

  const handleOpenDeleteModal = (doc) => { setSelectedDocForDelete(doc); setSelectedDocName(doc.DocumentName); setShowDeleteModal(true); };

  const handleDeleteDocument = async () => {
    const doc = selectedDocForDelete;
    if (!doc) return;
    try {
      const url = `${APIConstants.BASE_URL_BILLING}${NewEncounterConstants.DeleteClaimAttachment}?sInputJSON={DocumentID:${doc.DocumentID}}`;
      const r = await fetch(url, { method: "DELETE", headers: { "Content-Type": "application/json", Authorization: `Bearer ${sAuthToken}` } });
      if (!r.ok) throw new Error("fail");
      setSelectedDocs((p) => p.filter((d) => d.DocumentID !== doc.DocumentID));
      setShowDeleteModal(false); setSuccessDeleteModal(true);
      handleGetClaimAttachments();
    } catch {}
  };

  const handleRemoveSelectedDoc = (i) => { setSelectedDocs((p) => p.filter((_, idx) => idx !== i)); setShowErrorModal(true); };
  const cleanFileName = (n = "") => n.split("?")[0];

  const handleUploadClaimAttachments = async () => {
    if (!validateDocuments()) return;
    setLoading(true);
    const inp = {
      ClaimID: id || selectedClaimID, UploadedBy: userData?.userID, PracticeId: userData?.practiceID,
      PatientID: claimDetails?.PatientID || selectedPatientId,
      files: selectedDocs.map((f) => ({
        fileName: cleanFileName(f.fileName), fileContent: f.fileContent || "",
        PWK01: f.selectType || "", PWK02: f.selectMethod || "",
        PhyPath: f.PhyPath || "", WebPath: f.WebPath || "", Source: f.Source || "Unknown",
      })),
    };
    try {
      const r = await fetch(APIConstants.BASE_URL_BILLING + NewEncounterConstants.UploadClaimAttachments, {
        method: "POST", headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify(JSON.stringify(inp)),
      });
      if (r.status !== 200) return;
      const d = await r.json();
      if (d) setShowUploadModal(true);
      handleGetClaimAttachments();
      setSelectedDocs([]);
      setTimeout(() => setLoading(false), 100);
    } catch {}
  };

  const handleTypeChange = (i, v) => { const u = [...selectedDocs]; u[i].selectType = v; setSelectedDocs(u); };
  const handleMethodChange = (i, v) => { const u = [...selectedDocs]; u[i].selectMethod = v; setSelectedDocs(u); };

  const convertFileToBase64 = (file) =>
    new Promise((res, rej) => { const r = new FileReader(); r.readAsDataURL(file); r.onload = () => res(r.result.split(",")[1]); r.onerror = (e) => rej(e); });

  const handleLocalFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    const converted = await Promise.all(files.map(async (f) => ({
      DocumentName: f.name, fileContent: await convertFileToBase64(f), fileName: f.name,
      PWK01: selectType, PWK02: selectMethod, PhyPath: "", WebPath: "", Source: "NewUpload",
      selectType: "", selectMethod: "EL",
    })));
    setSelectedDocs((p) => [...p, ...converted]);
  };

  const handleGetClaimAttachmentTypes = async () => {
    const url = `${APIConstants.COMMON_BASE_URL}${NewEncounterConstants.GetClaimAttachmentTypes}`;
    fetch(url, { method: "GET", headers: { Accept: "application/json", "Content-Type": "application/json" } })
      .then((r) => (r.status !== 200 ? null : r.json()))
      .then((data) => { if (data) setattachmentTypeData(JSON.parse(data)); }).catch(() => {});
  };

  const GetProviderPDF = (item) => {
    const claimFilters = { PracticeID: userPracticeID, PatientID: item.PatientID, DOS: formatDOSDate(item.ClaimDOS), CPT: item.CPTOld };
    const sInputJSON = JSON.stringify({ objPatDetails: claimFilters });
    const url = `${APIConstants.PATIENT_PORTAL_URL}${CommonAPIConstants.CPTDcoument}?sInputJSON=${sInputJSON}`;
    fetch(url, { method: "GET", headers: { Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${sAuthToken}` } })
      .then((r) => r.json()).then((data) => { const u = JSON.parse(data); window.open(u.DocumentURL, "_blank"); }).catch(() => {});
  };

  const handleNewEncounterClick = () => window.open(`/#/patientdashboard/${selectedPatientId}/primary-insurance`, "_blank");

  /* Client-side encounter validator. Returns { errors, warnings, passed }
     each an array of { id, field, message, vid }. Advisory only — does not
     block Generate/Update; existing modal handles real blockers. */
  const runValidation = () => {
    const errors = [];
    const warnings = [];
    const passed = [];
    const NPI_RE = /^\d{10}$/;

    const need = (cond, id, field, message, vid, pushTo = errors) => {
      if (cond) passed.push({ id, field, message: "OK" });
      else pushTo.push({ id, field, message, vid });
    };

    // ── Layer 1: required fields ──
    need(!!searchPatientText?.trim(), "patient", "Patient", "Select a patient", "patient");
    need(!!appointmentDate, "appt", "Appointment Date", "Required", "appointmentDate");
    need(!!selectedPracticeProvider, "sched", "Scheduling Provider", "Required", "schedulingProvider");
    need(
      !!selectedInsurance && selectedInsurance !== "-1" && (selectedInsurance?.InsCoID || selectedInsurance?.insCoID),
      "priIns", "Primary Insurance", "Required", "primaryInsurance"
    );
    need(!!selectedLocation && selectedLocation !== "0", "loc", "Service Location Address (32)", "Required", "serviceLocAddr");
    need(!!selectedServiceLocation, "svcLoc", "Service Loc (24b)", "Required", "serviceLoc24b");
    need(!!selectedBillingNPI && selectedBillingNPI !== "0", "32a", "Service Location NPI (32a)", "Required", "serviceLocNPI");
    need(!!selectedCMS33a && selectedCMS33a !== "0", "33a", "Billing Provider NPI (33a)", "Required", "billingNPI33a");
    need(!!selectedCMS33b && selectedCMS33b !== "0", "33b", "Billing Provider Taxonomy (33b)", "Required", "taxonomy33b");

    // ── Layer 1: format checks ──
    if (selectedBillingNPI && selectedBillingNPI !== "0" && !NPI_RE.test(String(selectedBillingNPI))) {
      errors.push({ id: "32a-fmt", field: "Service Location NPI (32a)", message: "NPI must be exactly 10 digits", vid: "serviceLocNPI" });
    }
    if (selectedCMS33a && selectedCMS33a !== "0" && !NPI_RE.test(String(selectedCMS33a))) {
      errors.push({ id: "33a-fmt", field: "Billing Provider NPI (33a)", message: "NPI must be exactly 10 digits", vid: "billingNPI33a" });
    }

    // ── Layer 1: at least one CPT line with a procedure ──
    const linesWithProc = (rows || []).filter((r) => r.procedure && String(r.procedure).trim() !== "");
    if (linesWithProc.length === 0) {
      errors.push({ id: "cpt", field: "Service Lines (24D)", message: "Add at least one procedure (CPT) line", vid: "cptTable" });
    } else {
      passed.push({ id: "cpt", field: "Service Lines", message: `${linesWithProc.length} CPT line(s) entered` });
    }

    // ── Layer 1: at least one diagnosis somewhere (V1 requirement for Generate) ──
    const anyDx = (rows || []).some((r) => r.diag1 || r.diag2 || r.diag3 || r.diag4);
    if (!anyDx) {
      errors.push({ id: "dx", field: "Diagnosis (21)", message: "At least one diagnosis (Diag 1-4) is required", vid: "cptTable" });
    } else {
      passed.push({ id: "dx", field: "Diagnosis", message: "At least one diagnosis entered" });
    }

    // ── Layer 1: denied-claim resubmit gate ──
    if (denied == 1) {
      if (!selectedResubmitCode || selectedResubmitCode === "0") {
        errors.push({ id: "resubCode", field: "Resubmit Code (22)", message: "Required for denied claims", vid: "resubmitCode" });
      }
      if (!selectedOriginalClaim) {
        errors.push({ id: "origClaim", field: "Original Claim # (22)", message: "Required for denied claims", vid: "originalClaim" });
      }
    }

    // ── Layer 2: cross-field business rules ──
    // Each line with a procedure should also have at least one diagnosis pointer
    linesWithProc.forEach((r, i) => {
      if (!(r.diag1 || r.diag2 || r.diag3 || r.diag4)) {
        warnings.push({ id: `line-${i}-dx`, field: `Line ${i + 1} (${r.procedure})`, message: "No diagnosis pointer on this line", vid: "cptTable" });
      }
      if (!r.unit || parseFloat(r.unit) <= 0) {
        warnings.push({ id: `line-${i}-units`, field: `Line ${i + 1} (${r.procedure})`, message: "Units should be > 0", vid: "cptTable" });
      }
      const tc = parseFloat(r.totalCharge) || 0;
      if (tc === 0) {
        warnings.push({ id: `line-${i}-charge`, field: `Line ${i + 1} (${r.procedure})`, message: "Total charge is zero — confirm", vid: "cptTable" });
      } else {
        const expected = (parseFloat(r.unit) || 0) * (parseFloat(r.unitCharge) || 0);
        if (expected > 0 && Math.abs(expected - tc) > 0.01) {
          warnings.push({ id: `line-${i}-math`, field: `Line ${i + 1} (${r.procedure})`, message: `Total ($${tc}) ≠ units × charge ($${expected.toFixed(2)})`, vid: "cptTable" });
        }
      }
      // From/To dates not in the future, and To >= From
      if (r.fromDate && r.toDate) {
        const f = new Date(r.fromDate), t = new Date(r.toDate);
        if (!isNaN(f.getTime()) && !isNaN(t.getTime()) && t < f) {
          warnings.push({ id: `line-${i}-dates`, field: `Line ${i + 1}`, message: "Through Date is before From Date", vid: "cptTable" });
        }
      }
    });

    // DOS within primary insurance coverage window
    if (appointmentDate && selectedInsurance) {
      const dos = new Date(appointmentDate);
      const start = new Date(selectedInsurance.startDate || selectedInsurance.StartDate);
      const end = new Date(selectedInsurance.endDate || selectedInsurance.EndDate);
      if (!isNaN(dos.getTime()) && !isNaN(start.getTime()) && dos < start) {
        warnings.push({ id: "dos-start", field: "Appointment Date vs Primary", message: "DOS is before primary insurance start date", vid: "appointmentDate" });
      }
      if (!isNaN(dos.getTime()) && !isNaN(end.getTime()) && dos > end && end.getFullYear() !== 1900) {
        warnings.push({ id: "dos-end", field: "Appointment Date vs Primary", message: "DOS is after primary insurance end date", vid: "appointmentDate" });
      }
    }

    // Eligibility never checked
  

    // Through Date < From Date at header level
    if (startDate && endDate) {
      const s = new Date(startDate), e = new Date(endDate);
      if (!isNaN(s.getTime()) && !isNaN(e.getTime()) && e < s) {
        warnings.push({ id: "throughdate", field: "Through Date", message: "Through Date is before From Date", vid: "throughDate" });
      }
    }

    // Rendering provider missing (V1 doesn't strictly require — warn)
    if (!selectedRenderingProvider) {
      warnings.push({ id: "rendering", field: "Rendering Provider (24J)", message: "Rendering provider not selected", vid: "renderingProvider" });
    }

    // Secondary selected but primary skipped without secondary policy
    if (isPrimarySkipped && (!selectedSecInsurance || !selectedSecInsurance?.InsPolicyNumber)) {
      warnings.push({ id: "skipsec", field: "Secondary Insurance", message: "Primary skipped but secondary insurance details are missing", vid: "secondaryInsurance" });
    }

    setValidationResult({ errors, warnings, passed });
    setValidatedAtVersion(formVersion);
    setValidationOpen(true);
  };

  // Bump formVersion when any meaningful form field changes. Update / Resubmit
  // buttons stay disabled until runValidation snapshots this same version
  // again with zero errors.
  useEffect(() => {
    setFormVersion((v) => v + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appointmentDate, startDate, endDate, approveDate,
    selectedInsurance, selectedSecInsurance, isPrimarySkipped,
    selectedPracticeProvider, selectedRenderingProvider,
    selectedProvidersCategory, selectedFilterProvider,
    selectedLocation, selectedServiceLocation,
    selectedBillingNPI, selectedCMS33a, selectedCMS33b,
    selectedResubmitCode, selectedOriginalClaim,
    isBOX32checked, priorAuthority, claimNotes,
    rows,
  ]);

  // True only when the last validation run had zero errors AND no form edits
  // have happened since. Drives the disabled state of Update / Resubmit buttons.
  const validationPassed = !!validationResult
    && validationResult.errors.length === 0
    && validatedAtVersion === formVersion;

  /* Scroll the form to a field by its data-vid, then flash a highlight. */
  const scrollToValidationField = (vid) => {
    if (!vid) return;
    const el = document.querySelector(`[data-vid="${vid}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("enc-flash");
    setTimeout(() => el.classList.remove("enc-flash"), 1800);
  };

  /* Generates the CMS-1500 PDF for the current claim and opens it in a new
     tab. Same endpoint ClaimStatus uses (Billing/CMS1500Path). Requires an
     existing ClaimID — the button is hidden on brand-new encounters. */
  const handleGetCMS1500PDF = () => {
    if (!id) return;
    setCms1500Loading(true);
    const claimFilters = {
      PracticeID: userPracticeID,
      ClaimID: id,
      PatientID: claimDetails?.PatientID || selectedPatientId,
    };
    const sInputJSON = JSON.stringify(claimFilters);
    const url = `${APIConstants.BASE_URL_BILLING}${CommonAPIConstants.GetPDFCMS1500}?sInputJSON=${sInputJSON}`;
    fetch(url, { method: "GET", headers: { Accept: "application/json", "Content-Type": "application/json" } })
      .then((r) => r.json())
      .then((data) => {
        setCms1500Loading(false);
        if (!data) return;
        const parsed = typeof data === "string" ? JSON.parse(data) : data;
        if (parsed?.DocumentURL) window.open(parsed.DocumentURL, "_blank");
        else setErrorMessage("CMS-1500 PDF is not available for this claim yet.");
      })
      .catch(() => { setCms1500Loading(false); setErrorMessage("Failed to load CMS-1500 PDF."); });
  };

  const filteredDocs = allDocumentData.filter((d) =>
    d.DocDate.toLowerCase().includes(searchDocDate.toLowerCase()) &&
    d.DocumentName.toLowerCase().includes(searchDocName.toLowerCase()) &&
    d.DocTypeName.toLowerCase().includes(searchDocType.toLowerCase())
  );

  /* ─── Click-outside autocomplete close ─── */
  const suggestionRefs = useRef({});
  const cellAutocompleteRefs = useRef({});
  useEffect(() => {
    const onClick = (e) => {
      const updated = [...rows];
      let mod = false;
      rows.forEach((row, i) => {
        const c = suggestionRefs.current[i];
        if (c && !c.contains(e.target)) {
          if (row.hideList || row.hideMod || row.hideMod1 || row.hideMod2 || row.hideDiag || row.hideDiag1 || row.hideDiag2 || row.hideDiag3) {
            updated[i].hideList = false; updated[i].hideMod = false; updated[i].hideMod1 = false; updated[i].hideMod2 = false;
            updated[i].hideDiag = false; updated[i].hideDiag1 = false; updated[i].hideDiag2 = false; updated[i].hideDiag3 = false;
            mod = true;
          }
        }
      });
      if (mod) setRows(updated);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [rows]);

  /* ─── Decide which action buttons to show (parity with V1) ─── */
  /* Update / Resubmit For Approval are gated by validationPassed — the user
     must click Validate (with zero errors) before either button is enabled,
     and any form edit invalidates them again. Tooltip explains why. */
  const updateGateDisabled = !validationPassed;
  const updateGateTitle = updateGateDisabled
    ? (validationResult ? "Fix validation errors and re-run Validate to enable" : "Click Validate first to enable Update For Approval")
    : "";
  const UpdateBtn = ({ label = "Update For Approval", onClick = UpdateBtnclick }) => (
    <button
      className="enc-btn enc-btn-primary"
      onClick={onClick}
      disabled={updateGateDisabled}
      title={updateGateTitle}
    >
      {label}
    </button>
  );

  const renderActions = () => {
    if (isPartiallyDenied) {
      return (
        <>
          <button className="enc-btn enc-btn-sec" onClick={SaveSecondaryClaim}>Secondary Claims</button>
          <UpdateBtn label="Resubmit For Approval" onClick={CorrectPartiallyDeniedClaim} />
          <button className="enc-btn enc-btn-primary" onClick={GenerateNewClaims}>Generate New Claim</button>
        </>
      );
    }
    if (denied == 1) {
      return (
        <>
          <button className="enc-btn enc-btn-sec" onClick={SaveSecondaryClaim}>Secondary Claims</button>
          <UpdateBtn label="Resubmit For Approval" />
          {Object.values(isCheckboxChecked).some(Boolean) && (
            <button className="enc-btn enc-btn-primary" onClick={GenerateNewClaims}>Generate New Claim</button>
          )}
        </>
      );
    }
    if (btnStatus == 1 || btnStatus == 2) {
      return (
        <>
          <button className="enc-btn enc-btn-sec" onClick={SaveSecondaryClaim}>Secondary Claims</button>
          <UpdateBtn />
          {Object.values(isCheckboxChecked).some(Boolean) && (
            <button className="enc-btn enc-btn-primary" onClick={GenerateNewClaims}>Generate New Claim</button>
          )}
        </>
      );
    }
    if (btnStatus != null) {
      return (
        <>
          <UpdateBtn />
          {Object.values(isCheckboxChecked).some(Boolean) && (
            <button className="enc-btn enc-btn-primary" onClick={GenerateNewClaims}>Generate New Claim</button>
          )}
        </>
      );
    }
    return (
      <>
        {hasExistingClaim && <UpdateBtn />}
        {(!hasExistingClaim || Object.values(isCheckboxChecked).some(Boolean)) && (
          <button className="enc-btn enc-btn-primary" onClick={GenerateNewClaims}>Generate New Claim</button>
        )}
      </>
    );
  };

  /* =========================================================================
     RENDER
     ========================================================================= */
  return (
    <div className="hv-container hv-page hv-page--dense hv-page--fill">
      {isLoading && (
        <div className="enc-spinner-overlay"><div className="enc-spinner" /></div>
      )}

      <div>
        {/* ── Page header band ───────────────────────────────────────────── */}
        <div className="hv-pagehead">
          <div className="hv-inline">
            <div>
              <h1 className="hv-pagehead__title">{hasExistingClaim ? "Edit Encounter" : "New Encounter"}</h1>
              <div className="hv-pagehead__sub">
                {hasExistingClaim && <span className="hv-pill">Claim #{id}</span>}
                {claimDetails?.LastName && <span>{`${claimDetails.LastName}, ${claimDetails.FirstName}`}</span>}
                {selectedDOB && <span>DOB: {selectedDOB}</span>}
                {claimDetails?.ClaimDOS && <span>DOS: {formatDOSDate(claimDetails.ClaimDOS)}</span>}
                {denied == 1 && <span className="hv-pill hv-pill--danger">Denied</span>}
                {btnStatus == 1 && <span className="hv-pill hv-pill--success">Paid</span>}
                {btnStatus == 2 && <span className="hv-pill hv-pill--warning">Rejected</span>}
              </div>
            </div>
          </div>
          <div className="hv-pagehead__actions">
          
            <button
              className="hv-btn hv-btn--ghost"
              onClick={() => setCms1500PreviewOpen(true)}
              title="Preview the CMS-1500 form populated with current encounter data"
            >
              <FontAwesomeIcon icon={faFilePdf} /> CMS-1500 Preview
            </button>
            {hasExistingClaim && (
              <button
                className="hv-btn hv-btn--ghost"
                onClick={handleGetCMS1500PDF}
                disabled={cms1500Loading}
                title="Open the saved CMS-1500 PDF for this claim"
              >
                <FontAwesomeIcon icon={faFilePdf} />
                {cms1500Loading ? " Loading…" : " PDF"}
              </button>
            )}
            <div className="enc-total">
              <span className="enc-total-label">Total Charges (28)</span>
              <span className="enc-total-val">${grandTotal || "0.00"}</span>
            </div>
          </div>
        </div>

        {/* ── Patient Information ───────────────────────────────────────── */}
        <section className="hv-panel">
          <div className="hv-panel__head">
            <span className="hv-panel__heading">Patient Information</span>
            {patientSecInsData && Object.keys(patientSecInsData).length > 0 && (
              <span className="hv-pill"><FontAwesomeIcon icon={faShieldHalved} />Secondary on file</span>
            )}
          </div>
          <div className="hv-panel__body">
            <div className="enc-grid-3">
              <div className="enc-ff" data-vid="patient">
                <label><span className="enc-tag">(2)</span>Patient<span className="enc-req">*</span></label>
                <input
                  type="text"
                  className={!selectedPatientId ? "enc-bad" : "enc-good"}
                  value={searchPatientText || ""}
                  onChange={(e) => setSearchPatientText(e.target.value)}
                  placeholder="Search patient by name…"
                  autoComplete="off"
                />
                {Array.isArray(searchPatientData) && searchPatientData.length > 0 && (
                  <ul className="enc-autocomplete">
                    {searchPatientData.map((s) => (
                      <li key={s.val} onClick={() => handlePatientSuggestion(s)}>{s.label}</li>
                    ))}
                  </ul>
                )}
                {selectedPatientId && (
                  <div className="enc-patient-row">
                    <span className="enc-dm-link" onClick={handleNewEncounterClick}>Open DM</span>
                  </div>
                )}
              </div>

              <div className="enc-ff">
                <label><span className="enc-tag">(3)</span>Date of Birth</label>
                <span className="enc-value">{selectedDOB || "—"}</span>
              </div>

              <div className="enc-ff">
                <label><span className="enc-tag">(23)</span>Prior Authorization</label>
                <input type="text" value={priorAuthority || ""} onChange={(e) => setPriorAuthority(e.target.value)} />
              </div>
            </div>

            <div className="enc-grid-3">
              <div className="enc-ff" data-vid="primaryInsurance">
                <label><span className="enc-tag">(1)</span>Primary Insurance</label>
                <select
                  className="hv-select"
                  value={selectedInsurance?.insCoID ?? selectedInsurance?.InsCoID ?? "-1"}
                  onChange={handleInsuranceChange}
                >
                  <option value="-1">&lt;--Select--&gt;</option>
                  {Array.isArray(patientInsData) && patientInsData.map((ins) => (
                    <option key={ins?.insCoID || ins?.InsCoID} value={ins?.insCoID || ins?.InsCoID}>
                      {ins?.insConcatData || ins?.InsConcatData}
                    </option>
                  ))}
                </select>
                <div className="enc-hint">
                  {(formatInsDate(selectedInsurance?.startDate) || formatInsDate(selectedInsurance?.StartDate)) || "—"}
                  {" – "}
                  {(formatInsDate(selectedInsurance?.endDate) || formatInsDate(selectedInsurance?.EndDate)) || "—"}
                  {isInsdata && selectedInsData?.startDate && (
                    <> · {formatInsDate(selectedInsData.startDate)} – {formatInsDate(selectedInsData.endDate)}</>
                  )}
                </div>
                <div className="enc-inline-actions">
                  <button
                    className="hv-btn hv-btn--sm"
                    onClick={handleSkipPrimary}
                    disabled={isPrimarySkipped}
                  >
                    {isPrimarySkipped ? "Primary Skipped" : "Skip Primary"}
                  </button>
                </div>
              </div>

              <div className="enc-ff">
                <label><span className="enc-tag">(1a)</span>Primary Policy #</label>
                <span className="enc-value">{selectedPatientInsurance || "—"}</span>
                <div className="enc-inline-actions">
                  <button className="enc-btn enc-btn-sm" onClick={handlePrimaryIns} disabled={priEligLoading}>
                    {priEligLoading ? "Checking…" : "Check Eligibility"}
                  </button>
                  {priEligibility && (() => {
                    const ok = priEligibility.status?.toLowerCase() === "active" && !priEligibility.error;
                    return (
                      <span className={`enc-badge ${ok ? "ok" : "err"}`} title={priEligibility.error || priEligibility.status}>
                        {ok ? <><FontAwesomeIcon icon={faCircleCheck} /> {priEligibility.status || "Active"}</>
                            : (priEligibility.error || priEligibility.status || "Ineligible")}
                      </span>
                    );
                  })()}
                  {(priEligibility || showEligible) && selectedInsurance && (
                    <button className="hv-btn hv-btn--ghost hv-btn--sm" onClick={() => handleViewEligibilityDetails(selectedInsurance, "Primary")}>
                      View Details
                    </button>
                  )}
                  {!priEligibility && showEligible && <FontAwesomeIcon icon={faCircleCheck} />}
                </div>
              </div>

              <div className="enc-ff">
                <label>Submission Options</label>
                <label className="hv-check">
                  <input type="checkbox" id="chkIsPaerClaim" />
                  Do not send claim electronically
                </label>
              </div>
            </div>

            <div className="enc-grid-3">
              <div className="enc-ff" data-vid="secondaryInsurance">
                <label><span className="enc-tag">(9)</span>Secondary Insurance</label>
                <select
                  className="hv-select"
                  value={selectedSecInsurance?.insCoID ?? selectedSecInsurance?.InsCoID ?? selectedSecInsurance?.InsCoName ?? "-1"}
                  onChange={handleSecInsuranceChange}
                >
                  <option value="-1">&lt;--Select--&gt;</option>
                  {patientSecInsData && patientSecInsData.length > 0 && patientSecInsData.map((ins) => (
                    <option key={ins.insCoID ?? ins.InsCoID} value={ins.insCoID ?? ins.InsCoID}>
                      {ins.insCoName ?? ins.InsConcatData ?? ins?.InsCoName}
                    </option>
                  ))}
                </select>
                <div className="enc-hint">
                  {(formatInsDate(selectedSecInsurance?.startDate) || formatInsDate(selectedSecInsurance?.StartDate)) || "—"}
                  {" – "}
                  {(formatInsDate(selectedSecInsurance?.endDate) || formatInsDate(selectedSecInsurance?.EndDate)) || "—"}
                </div>
              </div>

              <div className="enc-ff">
                <label><span className="enc-tag">(9a)</span>Secondary Policy #</label>
                <span className="enc-value">
                  {selectedSecInsurance?.insPolicyNumber ?? selectedSecInsurance?.InsPolicyNumber ?? "—"}
                </span>
                <div className="enc-inline-actions">
                  <button className="enc-btn enc-btn-sm" onClick={handleSecIns} disabled={secEligLoading}>
                    {secEligLoading ? "Checking…" : "Check Eligibility"}
                  </button>
                  {secEligibility && (() => {
                    const ok = secEligibility.status?.toLowerCase() === "active" && !secEligibility.error;
                    return (
                      <span className={`enc-badge ${ok ? "ok" : "err"}`} title={secEligibility.error || secEligibility.status}>
                        {ok ? <><FontAwesomeIcon icon={faCircleCheck} /> {secEligibility.status || "Active"}</>
                            : (secEligibility.error || secEligibility.status || "Ineligible")}
                      </span>
                    );
                  })()}
                  {(secEligibility || showSecondaryEligible) && selectedSecInsurance && (
                    <button className="enc-btn-link" onClick={() => handleViewEligibilityDetails(selectedSecInsurance, "Secondary")}>
                      View Details
                    </button>
                  )}
                  {!secEligibility && showSecondaryEligible && <FontAwesomeIcon icon={faCircleCheck} style={{ color: "#15803d" }} />}
                </div>
              </div>
              <div />
            </div>
          </div>
        </section>

        {/* ── Dates & Provider ──────────────────────────────────────────── */}
        <section className="hv-panel">
          <div className="hv-panel__head"><span className="hv-panel__heading">Dates &amp; Provider</span></div>
          <div className="hv-panel__body">
            <div className="enc-grid-2">
              <div className="enc-grid-2">
                <div className="enc-ff" data-vid="appointmentDate">
                  <label>Appointment Date<span className="enc-req">*</span></label>
                  <CustomDatePicker
                    value={appointmentDate || (claimDetails?.ClaimDOS ? formatDOSDate(claimDetails?.ClaimDOS) : "")}
                    onChange={handleToAppointmentDateChange}
                    placeholderText="Select a date"
                    isReadOnly={false}
                    className={`enc-date-input ${appointmentDate ? "default-selected" : ""} ${!appointmentDate && !claimDetails?.ClaimDOS ? "enc-bad" : "enc-good"}`}
                  />
                </div>
                <div className="enc-ff">
                  <label>From Date<span className="enc-req">*</span></label>
                  <CustomDatePicker
                    value={startDate || (claimDetails?.ClaimDOS ? formatDOSDate(claimDetails?.ClaimDOS) : "")}
                    onChange={handleFromDateChange}
                    placeholderText="Select a date"
                    isReadOnly={false}
                    className={`enc-date-input ${startDate ? "default-selected" : ""} ${!startDate && !claimDetails?.ClaimDOS ? "enc-bad" : "enc-good"}`}
                  />
                </div>
                <div className="enc-ff" data-vid="throughDate">
                  <label>Through Date</label>
                  <CustomDatePicker
                    value={endDate || (claimDetails?.ClaimDOS ? formatDOSDate(claimDetails?.ClaimDOS) : "")}
                    onChange={handleToDateChange}
                    placeholderText="Select a date"
                    isReadOnly={false}
                    className={`enc-date-input ${endDate ? "default-selected" : ""}`}
                  />
                </div>
                <div className="enc-ff">
                  <label>To Approve</label>
                  <CustomDatePicker
                    value={approveDate || (claimDetails?.PostingDate ? formatDOSDate(claimDetails?.PostingDate) : "")}
                    onChange={handleToApproveDateChange}
                    placeholderText="Select a date"
                    isReadOnly={false}
                    className={`enc-date-input ${approveDate ? "default-selected" : ""}`}
                  />
                </div>
              </div>

              <div className="enc-grid-2">
                <div className="enc-ff" data-vid="schedulingProvider">
                  <label>Scheduling Provider<span className="enc-req">*</span></label>
                  <select
                    className={!selectedPracticeProvider ? "enc-bad" : "enc-good"}
                    value={selectedPracticeProvider || ""}
                    onChange={(e) => setSelectedPracticeProvider(e.target.value)}
                  >
                    <option value="">&lt;--Select--&gt;</option>
                    {practiceProvider.map((p, i) => (
                      <option value={p.ProviderID} key={i}>{p.ProviderName}</option>
                    ))}
                  </select>
                </div>
                <div className="enc-ff" data-vid="renderingProvider">
                  <label><span className="enc-tag">(24 J)</span>Rendering Provider</label>
                  <select value={selectedRenderingProvider || ""} onChange={(e) => setSelectedRenderingProvider(e.target.value)}>
                    <option value="">&lt;--Select--&gt;</option>
                    {practiceProvider.map((p, i) => (
                      <option value={p.ProviderID} key={i}>{p.ProviderName}</option>
                    ))}
                  </select>
                </div>
                <div className="enc-ff">
                  <label><span className="enc-tag">(17)</span>Supervising Type</label>
                  <select
                    value={selectedProvidersCategory || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSelectedProvidersCategory(v);
                      if (v === "DN") getPhysicianDropdown();
                      else getPracticeProvidersOnly();
                    }}
                  >
                    {providerCategory.map((p, i) => (<option value={p.Value} key={i}>{p.Label}</option>))}
                  </select>
                </div>
                <div className="enc-ff">
                  <label>Supervising Provider</label>
                  <select onChange={handleSelectFilterProviders} value={selectedFilterProvider || "-1"}>
                    <option value="-1">&lt;--Select--&gt;</option>
                    {(selectedProvidersCategory === "DN" ? physicianDropdownData : filteredProviders)?.map((p) => (
                      <option key={p.ID || p.ProviderID} value={p.ID || p.ProviderID}>
                        {p.FirstName || p.LastName
                          ? `${p.FirstName ?? ""} ${p.LastName ?? ""}`.trim() + (p.PhySpeciality?.trim() ? ` - ${p.PhySpeciality}` : "")
                          : p.ProviderName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Copay & Location ──────────────────────────────────────────── */}
        <section className="hv-panel">
          <div className="hv-panel__head"><span className="hv-panel__heading">(29) Copay &amp; Location</span></div>
          <div className="hv-panel__body">
            <div className="enc-grid-2">
              <div className="enc-ff">
                <label>Copay</label>
                <input type="text" />
              </div>
              <div className="enc-ff" data-vid="serviceLocAddr">
                <label><span className="enc-tag">(32)</span>Service Location Address<span className="enc-req">*</span></label>
                <select
                  className={selectedLocation === "0" || !selectedLocation ? "enc-bad" : "enc-good"}
                  value={selectedLocation || "0"}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                >
                  <option value="0">&lt;--Select--&gt;</option>
                  {locations.map((it, i) => (
                    <option key={i} value={it.LocationId} title={`${it.LocationName}, ${it["Address (Main)"]}, ${it.City}, ${it.State}, ${it["Zip Code"]}`}>
                      {it.LocationName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="enc-grid-2" style={{ marginTop: 14 }}>
              <div className="enc-ff">
                <label><span className="enc-tag">(19)</span>Additional Claim Information</label>
                <textarea value={claimNotes || ""} onChange={(e) => setclaimNotes(e.target.value)} rows={2} />
              </div>
              <div className="enc-ff">
                <label><span className="enc-tag">(33)</span>Billing Address<span className="enc-req">*</span></label>
                <input type="text" className={!selectedBillingAddress ? "enc-bad" : "enc-good"} value={selectedBillingAddress || ""} onChange={(e) => setSelectedBillingAddress(e.target.value)} />
              </div>
            </div>
          </div>
        </section>

        {/* ── NPI & CMS ─────────────────────────────────────────────────── */}
        <section className="hv-panel">
          <div className="hv-panel__head"><span className="hv-panel__heading">Location &amp; NPI</span></div>
          <div className="hv-panel__body">
            <div className="enc-grid-2">
              <div className="enc-ff" data-vid="serviceLoc24b">
                <label><span className="enc-tag">(24 b)</span>Service Loc</label>
                <select value={selectedServiceLocation || ""} onChange={handleSelectChange}>
                  <option value="">&lt;--Select--&gt;</option>
                  {ServiceLocations.map((it, i) => (
                    <option key={i} value={it.ID}>{it.PlaceOfService}</option>
                  ))}
                </select>
              </div>
              <div className="enc-ff" data-vid="serviceLocNPI">
                <label><span className="enc-tag">(32 a)</span>Service Location NPI<span className="enc-req">*</span></label>
                <select
                  className={!selectedBillingNPI || selectedBillingNPI === "0" ? "enc-bad" : "enc-good"}
                  value={selectedBillingNPI || "0"}
                  onChange={handleSelectBillingNPI}
                >
                  <option value="0">&lt;--Select--&gt;</option>
                  {BillingNPIs.map((it, i) => (
                    <option value={it.NPI} key={i}>{it.NamewithNPI}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="enc-grid-2" style={{ marginTop: 14 }}>
              <div className="enc-ff" data-vid="billingNPI33a">
                <label><span className="enc-tag">(33 a)</span>Billing Provider NPI<span className="enc-req">*</span></label>
                <select
                  className={!selectedCMS33a || selectedCMS33a === "0" ? "enc-bad" : "enc-good"}
                  value={selectedCMS33a || "0"}
                  onChange={(e) => setSelectedCMS33a(e.target.value)}
                >
                  <option value="0">&lt;--Select--&gt;</option>
                  {cms33aData.map((it, i) => (
                    <option value={it.NPI || it.claim33a} key={i}>{it.NamewithNPI}</option>
                  ))}
                </select>
              </div>
              <div className="enc-ff" data-vid="taxonomy33b">
                <label><span className="enc-tag">(33 b)</span>Billing Provider Taxonomy<span className="enc-req">*</span></label>
                <select
                  className={!selectedCMS33b || selectedCMS33b === "0" ? "enc-bad" : "enc-good"}
                  value={selectedCMS33b || "0"}
                  onChange={(e) => setSelectedCMS33b(e.target.value)}
                >
                  <option value="0">&lt;--Select--&gt;</option>
                  {cms33bData.map((it, i) => (
                    <option key={i} value={it.TaxonomyCode || it.claim33b}>{it.NamewithTaxCode}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Quick-select practice buttons */}
          {BillingNPIs.some((b) => b.PracticeLabel) && (
            <div className="enc-practice-row">
              {BillingNPIs.map((it, i) => it.PracticeLabel ? (
                <button key={i} className="enc-practice-btn" onClick={() => handlePracticeButtonClick(it)}>{it.PracticeLabel}</button>
              ) : null)}
            </div>
          )}

          {/* BOX32 / resubmit / original claim */}
          <div className="enc-meta-strip">
            <label className="enc-check">
              <input type="checkbox" checked={!!isBOX32checked} onChange={(e) => setIsBox32Checked(e.target.checked)} />
              Omit BOX 32
            </label>
            <div className="enc-ff" data-vid="resubmitCode">
              <strong>(22) Resubmit code<span className="enc-req">*</span>:</strong>
              <select
                className={`hv-select ${!selectedResubmitCode || selectedResubmitCode === "0" ? "enc-bad" : "enc-good"}`}
                value={selectedResubmitCode || "0"}
                onChange={(e) => setSelectedResubmitCode(e.target.value)}
              >
                <option value="0">N/A</option>
                <option value="6">6</option>
                <option value="7">7</option>
                <option value="8">8</option>
                <option value="137">137</option>
              </select>
            </div>
            <div className="enc-ff" data-vid="originalClaim">
              <strong>Original claim #:</strong>
              <input
                className="hv-input"
                type="text"
                value={selectedOriginalClaim || claimDetails?.PayerRefNumber || ""}
                onChange={(e) => setSelectedOriginalClaim(e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* ── CPT Lines (24) ────────────────────────────────────────────── */}
        {isPatientSelected && (
          <section className="hv-panel hv-panel--flush" data-vid="cptTable">
            <div className="hv-panel__head">
              <span className="hv-panel__heading">(24) Service Lines</span>
              <span className="hv-pill">{filteredRows?.length || 0} line(s)</span>
            </div>
            <div className="hv-table-scroll hv-table-scroll--fade">
                <table className="hv-table hv-worksheet hv-worksheet--pinned hv-table--striped">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>From</th>
                      <th>To</th>
                      <th>Claim ID<div><input className="hv-cellinput" type="text" placeholder="Search" value={claimIdSearch} onChange={(e) => setClaimIdSearch(e.target.value)} /></div></th>
                      <th>New Claim ID<div><input className="hv-cellinput" type="text" placeholder="Search" value={newClaimIdSearch} onChange={(e) => setNewClaimIdSearch(e.target.value)} /></div></th>
                      <th>Provider<div><input className="hv-cellinput" type="text" placeholder="Search" value={providerNameSearch} onChange={(e) => setProviderNameSearch(e.target.value)} /></div></th>
                      <th>Procedure (24D)<div><input className="hv-cellinput" type="text" placeholder="Search" value={procedureSearch} onChange={(e) => setProcedureSearch(e.target.value)} /></div></th>
                      <th>Mod1</th>
                      <th>Mod2</th>
                      <th>Mod3</th>
                      <th>Diag1</th>
                      <th>Diag2</th>
                      <th>Diag3</th>
                      <th>Diag4</th>
                      <th>Unit (24G)</th>
                      <th>Unit Chg (24F)</th>
                      <th>Apply Pmt</th>
                      <th>Total Charge</th>
                      <th className="enc-th-recv">Recv Pri</th>
                      <th className="enc-th-recv">Recv Sec</th>
                      <th>Contr Adj</th>
                      <th>Sec Pending</th>
                      <th>Deductible</th>
                      <th>Pat Rs</th>
                      <th>Other Adj</th>
                      <th>Fed Red</th>
                      <th>Write-off</th>
                      <th>Resubmit</th>
                      <th>Pri Dx</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows?.map((row, index) => {
                      const claimRow = claimRowsData[index];
                      const disabled = row.IsCPTMoved == 1;
                      const isPaid = claimRow?.recvdCharge > 0;
                      return (
                        <tr
                          key={index}
                          ref={(el) => (suggestionRefs.current[index] = el)}
                          className={`${disabled ? "enc-row-disabled" : ""} ${isPaid ? "enc-row-paid" : ""}`}
                          title={disabled ? `Moved to Claim ${claimRowsData[index]?.MovedToClaimID}` : ""}
                        >
                          <td>{index + 1}</td>
                          <td>
                            <CustomDatePicker
                              value={row?.fromDate}
                              onChange={(d) => setRows((p) => { const u = [...p]; u[index].fromDate = d; return u; })}
                              placeholderText="MM/DD/YYYY"
                              isReadOnly={disabled}
                            />
                          </td>
                          <td>
                            <CustomDatePicker
                              value={row?.toDate}
                              onChange={(d) => setRows((p) => { const u = [...p]; u[index].toDate = d; return u; })}
                              placeholderText="MM/DD/YYYY"
                              isReadOnly={disabled}
                            />
                          </td>
                          <td>{row?.ClaimId}</td>
                          <td>{row?.NewClaimId}</td>
                          <td>
                            <div className="enc-inline-actions">
                              <span>{row?.providername}</span>
                              {id && (
                                <FontAwesomeIcon
                                  onClick={() => GetProviderPDF(claimRow)}
                                  icon={faFilePdf}
                                />
                              )}
                            </div>
                          </td>
                          <td className="enc-cell-autocomplete-wrap">
                            <input
                              ref={(el) => (cellAutocompleteRefs.current[`cpt-${index}`] = el)}
                              type="text"
                              className="hv-cellinput"
                              disabled={disabled}
                              placeholder="CPT"
                              value={row.procedure || ""}
                              onChange={(e) => handleCptChange(index, e.target.value)}
                            />
                            <FloatingCellAutocomplete
                              anchorRef={{ current: cellAutocompleteRefs.current[`cpt-${index}`] }}
                              open={row?.hideList && Array.isArray(row.selectedCPTs) && row.selectedCPTs.length > 0}
                            >
                                {Array.isArray(row.selectedCPTs) && row.selectedCPTs.map((s, i) => (
                                  <li key={i} onMouseDown={(e) => { e.preventDefault(); saveCpt(index, s); }}>{`${s?.CPTCode} - ${s?.CPTDesc}`}</li>
                                ))}
                            </FloatingCellAutocomplete>
                            <div>
                              <input
                                type="checkbox"
                                disabled={disabled}
                                checked={row.hasExistingClaim ? (isCheckboxChecked[row.ID] || btnStatus == null) : isCheckboxChecked[row.ID]}
                                onChange={() => handleCheckboxClick(row.ID)}
                              />
                            </div>
                          </td>
                          {/* Mods */}
                          {["mod1", "mod2", "mod3"].map((modKey, mi) => {
                            const hideKey = mi === 0 ? "hideMod" : mi === 1 ? "hideMod1" : "hideMod2";
                            return (
                              <td className="enc-cell-autocomplete-wrap" key={modKey}>
                                <input
                                  ref={(el) => (cellAutocompleteRefs.current[`mod-${index}-${modKey}`] = el)}
                                  type="text"
                                  placeholder={`Mod ${mi + 1}`}
                                  disabled={disabled}
                                  className="hv-cellinput"
                                  value={row[modKey] || ""}
                                  onChange={(e) => handleModChange(e.target.value, modKey, index, hideKey)}
                                />
                                <FloatingCellAutocomplete
                                  anchorRef={{ current: cellAutocompleteRefs.current[`mod-${index}-${modKey}`] }}
                                  open={row[hideKey] && Array.isArray(row.modDataList) && row.modDataList.length > 0}
                                >
                                    {Array.isArray(row.modDataList) && row.modDataList.map((s, i) => (
                                      <li key={i} onMouseDown={(e) => { e.preventDefault(); saveTableData(index, modKey, s?.val, hideKey); }}>{s?.val}</li>
                                    ))}
                                </FloatingCellAutocomplete>
                              </td>
                            );
                          })}
                          {/* Diags */}
                          {["diag1", "diag2", "diag3", "diag4"].map((dk, di) => {
                            const hideKey = di === 0 ? "hideDiag" : di === 1 ? "hideDiag1" : di === 2 ? "hideDiag2" : "hideDiag3";
                            return (
                              <td className="enc-cell-autocomplete-wrap" key={dk}>
                                <input
                                  ref={(el) => (cellAutocompleteRefs.current[`diag-${index}-${dk}`] = el)}
                                  type="text"
                                  className="hv-cellinput"
                                  disabled={disabled}
                                  placeholder={`Diag ${di + 1}`}
                                  value={row[dk] || ""}
                                  onChange={(e) => handleDiagChange(e.target.value, dk, index, hideKey)}
                                />
                                <FloatingCellAutocomplete
                                  anchorRef={{ current: cellAutocompleteRefs.current[`diag-${index}-${dk}`] }}
                                  open={row[hideKey] && Array.isArray(row.diagDataList) && row.diagDataList.length > 0}
                                >
                                    {Array.isArray(row.diagDataList) && row.diagDataList.map((s, i) => (
                                      <li key={i} onMouseDown={(e) => { e.preventDefault(); saveTableData(index, dk, s?.Value, hideKey); }}>{s?.Value}</li>
                                    ))}
                                </FloatingCellAutocomplete>
                              </td>
                            );
                          })}
                          <td>
                            <input
                              className="hv-cellinput num"
                              disabled={disabled}
                              value={row?.unit || ""}
                              onChange={(e) => {
                                const u = e.target.value;
                                saveTableData(index, "unit", u);
                                saveTableData(index, "totalCharge", u * row?.unitCharge);
                                updateGrandTotal();
                              }}
                            />
                          </td>
                          <td>
                            <input
                              className="hv-cellinput num"
                              disabled={disabled}
                              value={row?.unitCharge || ""}
                              onChange={(e) => {
                                const uc = e.target.value;
                                saveTableData(index, "unitCharge", uc);
                                saveTableData(index, "totalCharge", row?.unit * uc);
                                updateGrandTotal();
                              }}
                            />
                          </td>
                          <td><input className="hv-cellinput" disabled={disabled} /></td>
                          <td>
                            <input
                              className="hv-cellinput num"
                              disabled={disabled}
                              value={row?.totalCharge || ""}
                              onChange={(e) => { saveTableData(index, "totalCharge", e.target.value); updateGrandTotal(); }}
                            />
                          </td>
                          <td className="enc-td-recv">{row?.recvdCharge ?? "—"}</td>
                          <td className="enc-td-recv">{row?.SecrecvdCharge ?? "—"}</td>
                          <td>
                            <input className="hv-cellinput num" disabled={disabled} value={row?.Adjustment || ""} onChange={(e) => saveTableData(index, "Adjustment", e.target.value)} />
                          </td>
                          <td>
                            <input className="hv-cellinput num" disabled={disabled} value={row?.SecPendingAmt || ""} onChange={(e) => saveTableData(index, "SecPendingAmt", e.target.value)} />
                          </td>
                          <td>
                            <input className="hv-cellinput num" disabled={disabled} value={row?.Deductible || ""} onChange={(e) => saveTableData(index, "Deductible", e.target.value)} />
                          </td>
                          <td>
                            <input className="hv-cellinput num" disabled={disabled} value={row?.PatientAmount || ""} onChange={(e) => saveTableData(index, "PatientAmount", e.target.value)} />
                          </td>
                          <td>{row?.OtherAdjustment ?? "—"}</td>
                          <td>{row?.FederalReduction ?? "—"}</td>
                          <td>
                            <input className="hv-cellinput num" disabled={disabled} value={row?.AdminWriteOff || ""} onChange={(e) => saveTableData(index, "AdminWriteOff", e.target.value)} />
                          </td>
                          <td className="num">
                            <input type="checkbox" disabled={disabled} />
                          </td>
                          <td>
                            <input className="hv-cellinput num" disabled={disabled} value={row?.ClaimCPTLineNumber || ""} onChange={(e) => saveTableData(index, "ClaimCPTLineNumber", e.target.value)} />
                          </td>
                          <td>
                            <div className="enc-row-actions">
                              <button className="hv-icon-btn" title="Add line" onClick={() => checkThenAddRow(row)}><FontAwesomeIcon icon={faPlus} /></button>
                              <button className="hv-icon-btn hv-btn--danger" title="Remove line" onClick={() => handleRemoveRow(index, row)}><FontAwesomeIcon icon={faMinus} /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={16} className="num">Total Charges (28)</td>
                      <td className="num">${grandTotal || "0.00"}</td>
                      <td colSpan={13} />
                    </tr>
                  </tfoot>
                </table>
              </div>
          </section>
        )}

        {/* ── Attachments ───────────────────────────────────────────────── */}
        {isPatientSelected && (
          <section className="hv-panel hv-panel--flush">
            <div className="hv-panel__head">
              <span className="hv-panel__heading"><FontAwesomeIcon icon={faPaperclip} /> Attachments</span>
              <span className="hv-pill">{attachmentsData.length} on claim</span>
            </div>
            <div className="hv-table-scroll hv-table-scroll--fade">
              <table className="hv-table hv-worksheet hv-table--striped">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Document Name</th>
                    <th>Preview</th>
                    <th>Uploaded Date</th>
                    <th>Type</th>
                    <th>Method</th>
                    <th>ACN</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {attachmentsData.length === 0 ? (
                    <tr><td colSpan={8}>No attachments found</td></tr>
                  ) : attachmentsData.map((item, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>{item.DocumentName}</td>
                      <td><button className="hv-icon-btn" onClick={() => GetDOCPDF(item.FileUrl)}><FontAwesomeIcon icon={faFilePdf} /></button></td>
                      <td>{formatDOSDate(item.UploadedDate)}</td>
                      <td>{item.PWK01}</td>
                      <td>{item.PWK02}</td>
                      <td>{item.PWK06}</td>
                      <td>
                        <button className="hv-icon-btn hv-btn--danger" onClick={() => handleOpenDeleteModal(item)}>
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="enc-upload-zone">
              <input className="enc-file-input" type="file" multiple onChange={handleLocalFileUpload} />
              <span className="enc-or">OR</span>
              <button className="hv-btn hv-btn--ghost" onClick={handleGetPatientAllDocumentDetails}>Add Patient Documents</button>
              {selectedDocs.length > 0 && id && (
                <button className="hv-btn" onClick={handleUploadClaimAttachments}>Upload Attachments</button>
              )}
            </div>

            {selectedDocs.length > 0 && selectedDocs.map((doc, i) => (
              <div className="enc-doc-pending" key={i}>
                <strong>{doc?.fileName}</strong>
                <select className="hv-select" value={doc.selectType ?? ""} onChange={(e) => handleTypeChange(i, e.target.value)}>
                  <option value="">&lt;--Type--&gt;</option>
                  {attachmentTypeData.map((it, k) => (
                    <option value={it.ClaimAttachmentType} key={k}>{it.ClaimAttachmentType} - {it.ClaimAttachmentTypeDescription}</option>
                  ))}
                </select>
                <select className="hv-select" value={doc.selectMethod} onChange={(e) => handleMethodChange(i, e.target.value)}>
                  <option value="">Select Method</option>
                  <option value="AA">AA - Available on Request</option>
                  <option value="BM">BM - By Mail</option>
                  <option value="EL">EL - Electronically Submitted</option>
                  <option value="FX">FX - By Fax</option>
                </select>
                <button className="hv-icon-btn hv-btn--danger" onClick={() => handleRemoveSelectedDoc(i)}>
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            ))}
          </section>
        )}

        {/* ── Validation message ───────────────────────────────────────── */}
        {claimDetails?.DataValidationMessage && (
          <div className="enc-alert">{claimDetails.DataValidationMessage}</div>
        )}

        {/* ── Bottom actions ───────────────────────────────────────────── */}
        {isPatientSelected && hasExistingClaim && !validationPassed && (
          <div className="enc-alert" style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <FontAwesomeIcon icon={faCircleCheck} style={{ color: "#e8812a", fontSize: 16 }} />
            <div style={{ flex: 1 }}>
              <strong>Validate before submitting.</strong>{" "}
              {validationResult && validationResult.errors.length > 0
                ? `Fix the ${validationResult.errors.length} error${validationResult.errors.length === 1 ? "" : "s"} in the validation panel — Update / Resubmit For Approval are disabled until validation passes.`
                : "Click the Validate button at the top-right of the page to confirm all required fields are correct. Update / Resubmit For Approval will activate once validation passes."}
            </div>
          </div>
        )}
        {isPatientSelected && (
          <div className="enc-action-bar">
            <button
              className="enc-btn"
              onClick={runValidation}
              style={{ background: "#e8812a", color: "#fff", border: "1px solid #e8812a", fontWeight: 700 }}
              title="Check all required fields and business rules before submit"
            >
              <FontAwesomeIcon icon={faCircleCheck} /> Validate
              {validationResult && (validationResult.errors.length > 0 || validationResult.warnings.length > 0) && (
                <span style={{ marginLeft: 6, background: "#fff", color: "#e8812a", borderRadius: 10, padding: "1px 7px", fontSize: 11 }}>
                  {validationResult.errors.length + validationResult.warnings.length}
                </span>
              )}
            </button>
            {renderActions()}
          </div>
        )}
      </div>

      {/* ─── Modals ─────────────────────────────────────────────────────── */}
      {showSuccessModal && (
        <div className="enc-modal-back" onClick={handleCloseModals}>
          <div className="enc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="enc-modal-head ok">
              <span>Success</span>
              <button className="enc-modal-close" onClick={handleCloseModals}><FontAwesomeIcon icon={faXmark} /></button>
            </div>
            <div className="enc-modal-body"><p>Claim updated successfully.</p></div>
            <div className="enc-modal-actions"><button className="enc-btn enc-btn-primary" onClick={handleCloseModals}>OK</button></div>
          </div>
        </div>
      )}

      {showPaidErrorModal && (
        <div className="enc-modal-back" onClick={handleCloseModals}>
          <div className="enc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="enc-modal-head warn"><span>Confirm</span></div>
            <div className="enc-modal-body"><p>This claim is already paid. Are you sure you want to edit it?</p></div>
            <div className="enc-modal-actions">
              <button className="enc-btn" onClick={handleCloseModals}>Cancel</button>
              <button className="enc-btn enc-btn-primary" onClick={handleAddRow}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {showConfirmDeleteModal && (
        <div className="enc-modal-back" onClick={handleCloseModals}>
          <div className="enc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="enc-modal-head warn"><span>Delete CPT</span></div>
            <div className="enc-modal-body"><p>Are you sure you want to delete CPT {selectedCPT}?</p></div>
            <div className="enc-modal-actions">
              <button className="enc-btn" onClick={handleCloseModals}>Cancel</button>
              <button className="enc-btn enc-btn-primary" onClick={DeleteClaimCPT}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {showConfirmSaveModal && (
        <div className="enc-modal-back" onClick={handleCloseModals}>
          <div className="enc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="enc-modal-head ok"><span>Deleted</span></div>
            <div className="enc-modal-body"><p>Claim CPT deleted successfully.</p></div>
            <div className="enc-modal-actions"><button className="enc-btn enc-btn-primary" onClick={handleCloseModals}>OK</button></div>
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="enc-modal-back" onClick={handleCloseModals}>
          <div className="enc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="enc-modal-head warn">
              <span>Validation</span>
              <button className="enc-modal-close" onClick={handleCloseModals}><FontAwesomeIcon icon={faXmark} /></button>
            </div>
            <div className="enc-modal-body">
              {errorMessage.split("\n").filter(Boolean).map((m, i) => (<p key={i}>{m}</p>))}
            </div>
            <div className="enc-modal-actions"><button className="enc-btn enc-btn-primary" onClick={handleCloseModals}>OK</button></div>
          </div>
        </div>
      )}

      {showUploadModal && (
        <div className="enc-modal-back" onClick={handleCloseModals}>
          <div className="enc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="enc-modal-head ok"><span>Uploaded</span></div>
            <div className="enc-modal-body"><p>All files uploaded successfully.</p></div>
            <div className="enc-modal-actions"><button className="enc-btn enc-btn-primary" onClick={handleCloseModals}>OK</button></div>
          </div>
        </div>
      )}

      {showErrorModal && (
        <div className="enc-modal-back" onClick={handleCloseModals}>
          <div className="enc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="enc-modal-head warn"><span>Removed</span></div>
            <div className="enc-modal-body"><p>File removed.</p></div>
            <div className="enc-modal-actions"><button className="enc-btn enc-btn-primary" onClick={handleCloseModals}>OK</button></div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="enc-modal-back" onClick={handleCloseModals}>
          <div className="enc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="enc-modal-head warn"><span>Delete Document</span></div>
            <div className="enc-modal-body"><p>Are you sure you want to delete {selectedDocName}?</p></div>
            <div className="enc-modal-actions">
              <button className="enc-btn" onClick={handleCloseModals}>No</button>
              <button className="enc-btn enc-btn-primary" onClick={handleDeleteDocument}>Yes</button>
            </div>
          </div>
        </div>
      )}

      {successDeleteModal && (
        <div className="enc-modal-back" onClick={handleCloseModals}>
          <div className="enc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="enc-modal-head ok"><span>Deleted</span></div>
            <div className="enc-modal-body"><p>File deleted successfully.</p></div>
            <div className="enc-modal-actions"><button className="enc-btn enc-btn-primary" onClick={handleCloseModals}>OK</button></div>
          </div>
        </div>
      )}

      {showValidationModal && (
        <div className="enc-modal-back" onClick={handleCloseModals}>
          <div className="enc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="enc-modal-head warn"><span>Missing Info</span></div>
            <div className="enc-modal-body"><p>Please select both Type and Method for all files before uploading.</p></div>
            <div className="enc-modal-actions"><button className="enc-btn enc-btn-primary" onClick={handleCloseModals}>OK</button></div>
          </div>
        </div>
      )}

      {/* Validation drawer — slides in from the right with categorized results.
          Click any error/warning to scroll to and flash-highlight that field. */}
      {validationOpen && validationResult && (
        <>
          <div className="enc-val-back" onClick={() => setValidationOpen(false)} />
          <aside className="enc-val-drawer">
            <div className="enc-val-head">
              <div>
                <div className="enc-val-title">Validation</div>
                <div className="enc-val-sub">
                  {validationResult.errors.length === 0 && validationResult.warnings.length === 0
                    ? "All checks passed — ready to submit"
                    : `${validationResult.errors.length} error${validationResult.errors.length === 1 ? "" : "s"}, ${validationResult.warnings.length} warning${validationResult.warnings.length === 1 ? "" : "s"}`}
                </div>
              </div>
              <button className="enc-val-close" onClick={() => setValidationOpen(false)}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <div className="enc-val-body">
              {validationResult.errors.length === 0 && validationResult.warnings.length === 0 && (
                <div className="enc-alert ok" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <FontAwesomeIcon icon={faCircleCheck} style={{ fontSize: 18 }} />
                  <div>
                    <strong>Looks good.</strong><br />
                    All {validationResult.passed.length} checks passed.
                  </div>
                </div>
              )}

              {validationResult.errors.length > 0 && (
                <div className="enc-val-section">
                  <div className="enc-val-section-head err">
                    <FontAwesomeIcon icon={faXmark} /> Errors ({validationResult.errors.length})
                  </div>
                  {validationResult.errors.map((item) => (
                    <div key={item.id} className="enc-val-item err" onClick={() => scrollToValidationField(item.vid)}>
                      <div className="enc-val-item-field">{item.field}</div>
                      <div className="enc-val-item-msg">{item.message}</div>
                      {item.vid && <div className="enc-val-item-link">Go to field →</div>}
                    </div>
                  ))}
                </div>
              )}

              {validationResult.warnings.length > 0 && (
                <div className="enc-val-section">
                  <div className="enc-val-section-head warn">
                    <FontAwesomeIcon icon={faCircleCheck} /> Warnings ({validationResult.warnings.length})
                  </div>
                  {validationResult.warnings.map((item) => (
                    <div key={item.id} className="enc-val-item warn" onClick={() => scrollToValidationField(item.vid)}>
                      <div className="enc-val-item-field">{item.field}</div>
                      <div className="enc-val-item-msg">{item.message}</div>
                      {item.vid && <div className="enc-val-item-link">Go to field →</div>}
                    </div>
                  ))}
                </div>
              )}

              {validationResult.passed.length > 0 && (
                <div className="enc-val-section">
                  <div className="enc-val-section-head ok">
                    <FontAwesomeIcon icon={faCircleCheck} /> Passed ({validationResult.passed.length})
                  </div>
                  {validationResult.passed.map((item) => (
                    <div key={item.id} className="enc-val-item ok">
                      <div className="enc-val-item-field">{item.field}</div>
                      <div className="enc-val-item-msg">{item.message}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="enc-val-foot">
              <button className="enc-btn" onClick={() => setValidationOpen(false)}>Close</button>
              <button className="enc-btn enc-btn-primary" onClick={runValidation}>Re-validate</button>
            </div>
          </aside>
        </>
      )}

      {/* CMS-1500 inline form preview — renders form state as the paper form layout */}
      {cms1500PreviewOpen && (() => {
        const fullName = [claimDetails?.LastName, claimDetails?.FirstName].filter(Boolean).join(", ") || searchPatientText || "—";
        const priPolicy = selectedPatientInsurance || "—";
        const priIns = selectedInsurance?.insConcatData || selectedInsurance?.InsConcatData || selectedInsurance?.InsCoName || "—";
        const secIns = selectedSecInsurance?.insCoName || selectedSecInsurance?.InsConcatData || selectedSecInsurance?.InsCoName || "—";
        const secPolicy = selectedSecInsurance?.insPolicyNumber || selectedSecInsurance?.InsPolicyNumber || "—";
        const renderingProviderName = (practiceProvider.find((p) => String(p.ProviderID) === String(selectedRenderingProvider))?.ProviderName) || "—";
        const schedulingProviderName = (practiceProvider.find((p) => String(p.ProviderID) === String(selectedPracticeProvider))?.ProviderName) || "—";
        const supervisingProviderName = (() => {
          const list = selectedProvidersCategory === "DN" ? physicianDropdownData : practiceProvider;
          const p = list?.find((x) => String(x.ID || x.ProviderID) === String(selectedFilterProvider));
          if (!p) return "—";
          return p.ProviderName || `${p.FirstName ?? ""} ${p.LastName ?? ""}`.trim() || "—";
        })();
        const billingNPI = selectedBillingNPI && selectedBillingNPI !== "0" ? selectedBillingNPI : "—";
        const billing33aNPI = selectedCMS33a && selectedCMS33a !== "0" ? selectedCMS33a : "—";
        const billing33bTax = selectedCMS33b && selectedCMS33b !== "0" ? selectedCMS33b : "—";
        const locationName = locations.find((l) => String(l.LocationId) === String(selectedLocation))?.LocationName || "—";
        const serviceLocPOS = ServiceLocations.find((s) => String(s.ID) === String(selectedServiceLocation))?.PlaceOfService || "—";
        const diagSet = [];
        rows.forEach((r) => { [r.diag1, r.diag2, r.diag3, r.diag4].forEach((d) => { if (d && !diagSet.includes(d)) diagSet.push(d); }); });
        const totalPaid = rows.reduce((s, r) => s + (parseFloat(r.recvdCharge) || 0) + (parseFloat(r.SecrecvdCharge) || 0), 0).toFixed(2);
        return (
          <div className="enc-modal-back" onClick={() => setCms1500PreviewOpen(false)}>
            <div className="enc-modal enc-modal-wide" style={{ maxWidth: 1000 }} onClick={(e) => e.stopPropagation()}>
              <div className="enc-modal-head">
                <span>CMS-1500 (02/12) — Form Preview</span>
                <button className="enc-modal-close" onClick={() => setCms1500PreviewOpen(false)}><FontAwesomeIcon icon={faXmark} /></button>
              </div>
              <div className="enc-modal-body" style={{ maxHeight: "78vh", overflowY: "auto", padding: 0, background: "#fafafa" }}>
                <div className="cms1500-form">
                  {/* Top carrier strip */}
                  <div className="cms1500-carrier">
                    <strong>CARRIER:</strong> {priIns}
                  </div>

                  {/* Row 1: Box 1 + 1a + 2 + 3 + 4 */}
                  <div className="cms1500-row" style={{ gridTemplateColumns: "2fr 2fr 3fr 2fr 3fr" }}>
                    <div className="cms1500-box"><span className="cms1500-no">1.</span><span className="cms1500-lbl">Insurance Type</span><span className="cms1500-val">{priIns}</span></div>
                    <div className="cms1500-box"><span className="cms1500-no">1a.</span><span className="cms1500-lbl">Insured's ID Number</span><span className="cms1500-val">{priPolicy}</span></div>
                    <div className="cms1500-box"><span className="cms1500-no">2.</span><span className="cms1500-lbl">Patient's Name (Last, First, MI)</span><span className="cms1500-val">{fullName}</span></div>
                    <div className="cms1500-box"><span className="cms1500-no">3.</span><span className="cms1500-lbl">Birth Date</span><span className="cms1500-val">{selectedDOB || "—"}</span></div>
                    <div className="cms1500-box"><span className="cms1500-no">4.</span><span className="cms1500-lbl">Insured's Name</span><span className="cms1500-val">{fullName}</span></div>
                  </div>

                  {/* Row 2: Box 9, 9a, 9d, 11, 11c */}
                  <div className="cms1500-row" style={{ gridTemplateColumns: "3fr 2fr 2fr 2fr 3fr" }}>
                    <div className="cms1500-box"><span className="cms1500-no">9.</span><span className="cms1500-lbl">Other Insured's Name</span><span className="cms1500-val">{secIns}</span></div>
                    <div className="cms1500-box"><span className="cms1500-no">9a.</span><span className="cms1500-lbl">Other Insured's Policy</span><span className="cms1500-val">{secPolicy}</span></div>
                    <div className="cms1500-box"><span className="cms1500-no">9d.</span><span className="cms1500-lbl">Insurance Plan/Program</span><span className="cms1500-val">{secIns !== "—" ? secIns : "—"}</span></div>
                    <div className="cms1500-box"><span className="cms1500-no">11.</span><span className="cms1500-lbl">Insured's Policy Group</span><span className="cms1500-val">{priPolicy}</span></div>
                    <div className="cms1500-box"><span className="cms1500-no">11c.</span><span className="cms1500-lbl">Insurance Plan Name</span><span className="cms1500-val">{priIns}</span></div>
                  </div>

                  {/* Row 3: Box 17, 17a, 17b, 18 */}
                  <div className="cms1500-row" style={{ gridTemplateColumns: "3fr 2fr 2fr 3fr" }}>
                    <div className="cms1500-box"><span className="cms1500-no">17.</span><span className="cms1500-lbl">Name of Referring Provider</span><span className="cms1500-val">{supervisingProviderName}</span></div>
                    <div className="cms1500-box"><span className="cms1500-no">17a.</span><span className="cms1500-lbl">Other ID #</span><span className="cms1500-val">{selectedProvidersCategory || "—"}</span></div>
                    <div className="cms1500-box"><span className="cms1500-no">17b.</span><span className="cms1500-lbl">NPI</span><span className="cms1500-val">{selectedFilterProvider !== "-1" ? selectedFilterProvider : "—"}</span></div>
                    <div className="cms1500-box"><span className="cms1500-no">18.</span><span className="cms1500-lbl">Hospitalization Dates</span><span className="cms1500-val">—</span></div>
                  </div>

                  {/* Row 4: Box 19 + 20 + 23 */}
                  <div className="cms1500-row" style={{ gridTemplateColumns: "5fr 1fr 2fr" }}>
                    <div className="cms1500-box"><span className="cms1500-no">19.</span><span className="cms1500-lbl">Additional Claim Information</span><span className="cms1500-val">{claimNotes || "—"}</span></div>
                    <div className="cms1500-box"><span className="cms1500-no">20.</span><span className="cms1500-lbl">Outside Lab?</span><span className="cms1500-val">No</span></div>
                    <div className="cms1500-box"><span className="cms1500-no">23.</span><span className="cms1500-lbl">Prior Auth #</span><span className="cms1500-val">{priorAuthority || "—"}</span></div>
                  </div>

                  {/* Row 5: Box 21 (Diagnosis) — 12 slots A–L */}
                  <div className="cms1500-row" style={{ gridTemplateColumns: "1fr" }}>
                    <div className="cms1500-box">
                      <span className="cms1500-no">21.</span>
                      <span className="cms1500-lbl">Diagnosis or Nature of Illness/Injury (ICD-10)</span>
                      <div className="cms1500-dx-grid">
                        {["A","B","C","D","E","F","G","H","I","J","K","L"].map((letter, idx) => (
                          <div key={letter} className="cms1500-dx-slot">
                            <span className="cms1500-dx-letter">{letter}.</span>
                            <span className="cms1500-dx-code">{(diagSet[idx]?.split(" - ")[0]) || diagSet[idx] || ""}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Row 6: Box 22 (Resubmission) */}
                  <div className="cms1500-row" style={{ gridTemplateColumns: "2fr 5fr" }}>
                    <div className="cms1500-box"><span className="cms1500-no">22.</span><span className="cms1500-lbl">Resubmission Code</span><span className="cms1500-val">{selectedResubmitCode && selectedResubmitCode !== "0" ? selectedResubmitCode : "—"}</span></div>
                    <div className="cms1500-box"><span className="cms1500-no">22.</span><span className="cms1500-lbl">Original Ref. No.</span><span className="cms1500-val">{selectedOriginalClaim || "—"}</span></div>
                  </div>

                  {/* Row 7: Box 24 — Service Lines */}
                  <div className="cms1500-services-head">
                    <span>24. A. Date(s) of Service</span><span>B. POS</span><span>C. EMG</span><span>D. CPT/HCPCS</span><span>Modifiers</span><span>E. Dx Ptr</span><span>F. $ Charges</span><span>G. Units</span><span>H. EPSDT</span><span>I. ID Qual</span><span>J. Rendering Provider NPI</span>
                  </div>
                  {rows && rows.length > 0 ? rows.map((r, i) => (
                    <div key={i} className="cms1500-services-row">
                      <span>{r.fromDate || "—"}<br />{r.toDate || ""}</span>
                      <span>{serviceLocPOS}</span>
                      <span>—</span>
                      <span><strong>{r.procedure || "—"}</strong></span>
                      <span>{[r.mod1, r.mod2, r.mod3].filter(Boolean).join(" ") || "—"}</span>
                      <span>{["A","B","C","D"].filter((_, idx) => [r.diag1, r.diag2, r.diag3, r.diag4][idx]).join(",") || "—"}</span>
                      <span>${r.totalCharge || "0.00"}</span>
                      <span>{r.unit || "—"}</span>
                      <span>—</span>
                      <span>NPI</span>
                      <span>{billingNPI}</span>
                    </div>
                  )) : <div className="cms1500-services-row" style={{ gridColumn: "span 11", textAlign: "center", color: "#888" }}>No service lines yet</div>}

                  {/* Row 8: Box 25, 26, 27, 28, 29, 30 */}
                  <div className="cms1500-row" style={{ gridTemplateColumns: "2fr 2fr 1fr 1.5fr 1.5fr 1.5fr" }}>
                    <div className="cms1500-box"><span className="cms1500-no">25.</span><span className="cms1500-lbl">Federal Tax ID</span><span className="cms1500-val">{billing33aNPI}</span></div>
                    <div className="cms1500-box"><span className="cms1500-no">26.</span><span className="cms1500-lbl">Patient Account No.</span><span className="cms1500-val">{claimDetails?.PatientID || selectedPatientId || "—"}</span></div>
                    <div className="cms1500-box"><span className="cms1500-no">27.</span><span className="cms1500-lbl">Accept Assignment</span><span className="cms1500-val">YES</span></div>
                    <div className="cms1500-box"><span className="cms1500-no">28.</span><span className="cms1500-lbl">Total Charge</span><span className="cms1500-val">${grandTotal || "0.00"}</span></div>
                    <div className="cms1500-box"><span className="cms1500-no">29.</span><span className="cms1500-lbl">Amount Paid</span><span className="cms1500-val">${totalPaid}</span></div>
                    <div className="cms1500-box"><span className="cms1500-no">30.</span><span className="cms1500-lbl">Rsvd for NUCC</span><span className="cms1500-val">—</span></div>
                  </div>

                  {/* Row 9: Box 31, 32, 33 */}
                  <div className="cms1500-row" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
                    <div className="cms1500-box">
                      <span className="cms1500-no">31.</span>
                      <span className="cms1500-lbl">Signature of Physician</span>
                      <span className="cms1500-val">{renderingProviderName}</span>
                    </div>
                    <div className="cms1500-box">
                      <span className="cms1500-no">32.</span>
                      <span className="cms1500-lbl">Service Facility Location</span>
                      <span className="cms1500-val">{locationName}</span>
                      <div style={{ marginTop: 6, display: "flex", gap: 10 }}>
                        <span><strong>32a.</strong> NPI: {billingNPI}</span>
                      </div>
                    </div>
                    <div className="cms1500-box">
                      <span className="cms1500-no">33.</span>
                      <span className="cms1500-lbl">Billing Provider Info</span>
                      <span className="cms1500-val">Scheduling: {schedulingProviderName}</span>
                      <div style={{ marginTop: 6, display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <span><strong>33a.</strong> NPI: {billing33aNPI}</span>
                        <span><strong>33b.</strong> Tax: {billing33bTax}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="enc-modal-actions">
                <button className="enc-btn" onClick={() => window.print()}>Print</button>
                <button className="enc-btn enc-btn-primary" onClick={() => setCms1500PreviewOpen(false)}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Eligibility details modal — 271 breakdown */}
      {eligDetailsOpen && (
        <div className="enc-modal-back" onClick={() => setEligDetailsOpen(false)}>
          <div className="enc-modal enc-modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="enc-modal-head">
              <span>{eligDetailsLabel ? `${eligDetailsLabel} Eligibility Details` : "Eligibility Details"}</span>
              <button className="enc-modal-close" onClick={() => setEligDetailsOpen(false)}><FontAwesomeIcon icon={faXmark} /></button>
            </div>
            <div className="enc-modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
              {eligDetailsLoading && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 0" }}>
                  <div className="enc-spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
                  <span>Fetching real-time eligibility from payer…</span>
                </div>
              )}
              {!eligDetailsLoading && eligDetails?.error && (
                <div className="enc-alert err">{eligDetails.error}</div>
              )}
              {!eligDetailsLoading && eligDetails && !eligDetails.error && (() => {
                const payer    = eligDetails.objPayer || {};
                const sub      = eligDetails.objSubscriber || {};
                const benefits = Array.isArray(eligDetails.objBenefit)    ? eligDetails.objBenefit    : [];
                const copays   = Array.isArray(eligDetails.objCopayment)  ? eligDetails.objCopayment  : [];
                const deducts  = Array.isArray(eligDetails.objDeductible) ? eligDetails.objDeductible : [];
                const coins    = Array.isArray(eligDetails.objCoInsurace) ? eligDetails.objCoInsurace : [];
                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    {/* Summary strip */}
                    <div className="ds-vital-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
                      <div className="ds-vital-card">
                        <div className="ds-vital-label">Status</div>
                        <div className="ds-vital-value" style={{ fontSize: 14 }}>
                          {benefits[0]?.BenefitInfo || "—"}
                        </div>
                      </div>
                      <div className="ds-vital-card">
                        <div className="ds-vital-label">Copay</div>
                        <div className="ds-vital-value">${copays[0]?.BenefitAmount || "0"}</div>
                        <div className="ds-vital-unit">{copays[0]?.ServiceType || "per visit"}</div>
                      </div>
                      <div className="ds-vital-card">
                        <div className="ds-vital-label">Deductible</div>
                        <div className="ds-vital-value">${deducts[0]?.BenefitAmount || "0"}</div>
                        <div className="ds-vital-unit">{deducts[0]?.CoverageLevel || ""}</div>
                      </div>
                      <div className="ds-vital-card">
                        <div className="ds-vital-label">Coinsurance</div>
                        <div className="ds-vital-value">{coins[0]?.Percentage ? `${coins[0].Percentage}%` : "—"}</div>
                        <div className="ds-vital-unit">{coins[0]?.ServiceType || ""}</div>
                      </div>
                    </div>

                    {/* Payer + Subscriber */}
                    <div className="enc-grid-2">
                      <div className="hv-panel hv-panel--flush">
                        <div className="hv-panel__head"><span className="hv-panel__heading">Payer</span></div>
                        <div className="hv-panel__body" style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: "6px 12px", fontSize: 12.5 }}>
                          <strong>Name:</strong><span>{payer.PayerName || "—"}</span>
                          <strong>Payer ID:</strong><span>{payer.PayerID || "—"}</span>
                        </div>
                      </div>
                      <div className="hv-panel hv-panel--flush">
                        <div className="hv-panel__head"><span className="hv-panel__heading">Subscriber</span></div>
                        <div className="hv-panel__body" style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: "6px 12px", fontSize: 12.5 }}>
                          <strong>Name:</strong><span>{[sub.FirstName, sub.LastName].filter(Boolean).join(" ") || "—"}</span>
                          <strong>Member ID:</strong><span>{sub.MemberID || sub.MemberId || "—"}</span>
                          <strong>DOB:</strong><span>{sub.DOB || "—"}</span>
                          <strong>Gender:</strong><span>{sub.Gender || "—"}</span>
                        </div>
                      </div>
                    </div>

                    {/* Coverage / benefits table */}
                    {benefits.length > 0 && (
                      <div className="hv-panel hv-panel--flush">
                        <div className="hv-panel__head"><span className="hv-panel__heading">Coverage / Benefits</span></div>
                        <div className="hv-table-scroll hv-table-scroll--fade">
                          <table className="hv-table hv-table--striped">
                            <thead>
                              <tr>
                                <th>Benefit</th>
                                <th>Coverage</th>
                                <th>Service Type</th>
                                <th>Insurance Type</th>
                                <th>Plan Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              {benefits.map((b, i) => (
                                <tr key={i}>
                                  <td>{b.BenefitInfo || "—"}</td>
                                  <td>{b.CoverageLevel || "—"}</td>
                                  <td>{b.ServiceType || "—"}</td>
                                  <td>{b.InsuranceType || "—"}</td>
                                  <td>{b.PlanCoverageDescription || "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Copay / Deductible / Coinsurance breakdown */}
                    {(copays.length > 0 || deducts.length > 0 || coins.length > 0) && (
                      <div className="hv-panel hv-panel--flush">
                        <div className="hv-panel__head"><span className="hv-panel__heading">Patient Financial Responsibility</span></div>
                        <div className="hv-table-scroll hv-table-scroll--fade">
                          <table className="hv-table hv-table--striped">
                            <thead>
                              <tr>
                                <th>Type</th>
                                <th>Coverage</th>
                                <th>Service Type</th>
                                <th>Amount / %</th>
                                <th>Plan Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              {copays.map((c, i) => (
                                <tr key={`cp-${i}`}>
                                  <td><span className="enc-badge info">Copay</span></td>
                                  <td>{c.CoverageLevel || "—"}</td>
                                  <td>{c.ServiceType || "—"}</td>
                                  <td>${c.BenefitAmount || "0"}</td>
                                  <td>{c.PlanCoverageDescription || "—"}</td>
                                </tr>
                              ))}
                              {deducts.map((d, i) => (
                                <tr key={`dd-${i}`}>
                                  <td><span className="enc-badge warn">Deductible</span></td>
                                  <td>{d.CoverageLevel || "—"}</td>
                                  <td>{d.ServiceType || "—"}</td>
                                  <td>${d.BenefitAmount || "0"}</td>
                                  <td>{d.PlanCoverageDescription || "—"}</td>
                                </tr>
                              ))}
                              {coins.map((c, i) => (
                                <tr key={`ci-${i}`}>
                                  <td><span className="enc-badge ok">Coinsurance</span></td>
                                  <td>{c.CoverageLevel || "—"}</td>
                                  <td>{c.ServiceType || "—"}</td>
                                  <td>{c.Percentage ? `${c.Percentage}%` : "—"}</td>
                                  <td>{c.PlanCoverageDescription || "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="enc-modal-actions">
              <button className="enc-btn enc-btn-primary" onClick={() => setEligDetailsOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Patient documents modal (draggable) */}
      {showEncModal && (
        <Draggable>
          <div className="enc-modal" style={{ position: "fixed", top: 80, left: "50%", transform: "translateX(-50%)", maxWidth: 760, zIndex: 600 }}>
            <div className="enc-modal-head">
              <span>Patient Documents</span>
              <button className="enc-modal-close" onClick={handleCloseModals}><FontAwesomeIcon icon={faXmark} /></button>
            </div>
            <div className="hv-table-scroll hv-table-scroll--fade">
              <table className="hv-table hv-table--striped">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>DOS<div><input className="hv-cellinput" type="text" placeholder="Search" value={searchDocDate} onChange={(e) => setSearchDocDate(e.target.value)} /></div></th>
                    <th>Document Name<div><input className="hv-cellinput" type="text" placeholder="Search" value={searchDocName} onChange={(e) => setSearchDocName(e.target.value)} /></div></th>
                    <th>Document Type<div><input className="hv-cellinput" type="text" placeholder="Search" value={searchDocType} onChange={(e) => setSearchDocType(e.target.value)} /></div></th>
                    <th>Preview</th>
                    <th><input type="checkbox" onChange={handleSelectDocAllChange} /></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocs.map((it, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>{it.DocDate}</td>
                      <td>{it.DocumentName}</td>
                      <td>{it.DocTypeName}</td>
                      <td><FontAwesomeIcon style={{ color: "#c0392b", cursor: "pointer" }} onClick={() => GetDOCPDF(it.DocWebPath)} icon={faFilePdf} /></td>
                      <td><input type="checkbox" checked={docAllCheckbox.includes(i)} onChange={() => handleDocRowCheckbox(i)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="enc-modal-actions">
              <button className="enc-btn" onClick={handleCloseModals}>Cancel</button>
              <button className="enc-btn enc-btn-primary" onClick={handleSelectDocuments}>Select</button>
            </div>
          </div>
        </Draggable>
      )}
    </div>
  );
};

export default Encounter;
